



import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { getSocket } from '../ChatPage/socket';

const STYLE_ID = 'admin-realtime-ui-bridge-style-v12';
const TARGET_ATTR = 'data-admin-realtime-id';
const OWNER_ATTR = 'data-admin-realtime-owner';
const TARGET_SIGNATURE_ATTR = 'data-admin-realtime-signature';
const ROUTE_CHECK_MS = 900;
const VALUE_DEBOUNCE_MS = 80;
const CURSOR_INTERVAL_MS = 65;
const CURSOR_ANCHOR_LIMIT = 4;
const CURSOR_MATCH_MIN_SCORE = 52;
const CURSOR_LERP_NEAR = 0.34;
const CURSOR_LERP_FAR = 0.52;
const CURSOR_SNAP_DISTANCE = 0.65;
const SCROLL_INTERVAL_MS = 90;
const CAMERA_INTERVAL_MS = 80;
const REMOTE_VISUAL_MS = 1200;
const REMOTE_SUPPRESS_MS = 180;
const AUTHORITATIVE_PENDING_TTL_MS = 8000;
const BLOCK_LOCK_HEARTBEAT_MS = 3200;
const BLOCK_LOCK_RELEASE_DELAY_MS = 360;
const BLOCK_LOCK_NOTICE_COOLDOWN_MS = 800;
const SELECT_VISUAL_MAX_OPTIONS = 12;
const SELECT_VISUAL_AUTO_CLOSE_MS = 15000;
const SELECT_CURSOR_HEARTBEAT_MS = 900;

const ADMIN_3D_ROOT_SELECTOR = '.adminProduct3DPreview';
const ADMIN_3D_CONFIRM_SELECTOR = '.adminProduct3DPreview__confirmActions';
const ADMIN_3D_SAFE_ACTION_WORDS = /(?:перейти без сохранения|остаться здесь|сбросить|добавить работу|добавить размер|добавить тег|дублировать|копировать|показать|скрыть|развернуть|свернуть|по умолчанию)/i;
const ADMIN_3D_SERVER_ACTION_WORDS = /(?:^\s*сохранить\s*$|сохранить и перейти)/i;

/*
 * ОДИН runtime, разные страницы.
 *
 * Новая ADMIN-страница НЕ описывает десятки кнопок. Обычные button/input/
 * select/details автоматически попадают в INTERACTIVE_SELECTOR и получают
 * semantic target. Для страницы задаются только:
 *   1) route matcher;
 *   2) один-два корневых контейнера;
 *   3) политика values/scroll/camera.
 *
 * data-admin-sync-key нужен только там, где React рендерит несколько
 * семантически одинаковых динамических контролов и generic matcher не может
 * гарантировать однозначность. Сложное состояние модалок/мастеров передаётся
 * компактным semantic `state` namespace, а не имитацией серверной мутации.
 */
const COMMON_ADMIN_REALTIME_ROOTS = [
    '.app-header__collapseToggle',
    '.adminProduct3DPreview',
    '.adminProduct3DStage',
    '[class*="adminProduct3DStage"]',
    '.MuiDialog-root',
    '.MuiModal-root',
    '.MuiPopover-root',
    '.MuiMenu-root',
    '[role="dialog"]',
    '[role="listbox"]',
    '[role="menu"]',
];

const definePageConfig = ({
    match,
    roots = [],
    syncValues = true,
    syncScroll = true,
    syncCamera = false,
} = {}) => ({
    match,
    roots: Array.from(new Set([
        ...COMMON_ADMIN_REALTIME_ROOTS,
        ...roots,
    ])),
    syncValues,
    syncScroll,
    syncCamera,
});

const PAGE_CONFIGS = {
    'catalog-review': definePageConfig({
        match: (pathname) => pathname.startsWith('/admin/catalog-review'),
        roots: [
            '.catalogReview',
            '.catalogReview__modalBackdrop',
            '.catalogReview__pricingReviewBackdrop',
        ],
        /*
         * CatalogReview больше НЕ имеет собственного cursor/hover/scroll/camera
         * transport. Весь визуальный realtime идёт только через этот runtime.
         * Значения form/draft остаются доменной синхронизацией CatalogReview,
         * чтобы не дублировать серверные mutations.
         */
        syncValues: false,
        syncScroll: true,
        syncCamera: true,
    }),
    'product-seo-price': definePageConfig({
        match: (pathname) => pathname.startsWith('/containerProductPage/product-seo-price'),
        roots: [
            '[data-admin-realtime-root="product-seo-price"]',
            '.productSeoPricePage',
            '.pricingPolicyPanel',
            '.blankPriceCatalogPanel',
            '.blankSupplierPricesTable',
            '.blankPriceEstimator',
            '[class*="productSeoPricePage"]',
            '[class*="pricingPolicy"]',
            '[class*="blankPrice"]',
            '[class*="blankSupplier"]',
            '[class*="adminProduct3DPreview"]',
        ],
        syncValues: true,
        syncScroll: true,
        syncCamera: true,
    }),
};

const INTERACTIVE_SELECTOR = [
    'button',
    'a[href]',
    'input',
    'select',
    'textarea',
    '[contenteditable="true"]',
    'details',
    'summary',
    '[role="button"]',
    '[role="tab"]',
    '[role="option"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[role="switch"]',
    '[role="menuitem"]',
    '[aria-pressed]',
    '[aria-selected]',
    '[aria-expanded]',
    '[data-catalog-scroll-id]',
    '[data-admin-sync-id]',
    '[data-admin-sync-key]',
].join(',');

const CLICK_SELECTOR = [
    'button',
    'a[href]',
    'summary',
    '[role="button"]',
    '[role="tab"]',
    '[role="option"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[role="switch"]',
    '[role="menuitem"]',
].join(',');

const EXCLUDE_SELECTOR = [
    '.app-header__realtimeBtn',
    '.adminRealtimePanel',
    '.adminRealtimePanel *',
    '.adminRealtimeUiCursorLayer',
    '.adminRealtimeUiCursorLayer *',
    '.adminRealtimeUiActivityLayer',
    '.adminRealtimeUiActivityLayer *',
    '[data-admin-realtime-ignore="true"]',
].join(',');

/*
 * Курсор больше не опирается на один случайный DOM-узел. Для каждой точки
 * строится цепочка якорей: control -> field/row/card -> explicit scope -> root.
 * На другом разрешении берём первый уверенно найденный якорь. Это повторяет
 * идею старого data-sales-cursor-scope, но работает и на динамических страницах.
 */
const CURSOR_SURFACE_SELECTOR = [
    '[data-admin-sync-key]',
    '[data-admin-sync-scope]',
    '[data-catalog-sync-scope]',
    '[data-admin-realtime-root]',
    '[data-catalog-scroll-id]',
    '[role="dialog"]',
    '[role="row"]',
    'tr',
    'article',
    'section',
    '[class*="__field"]',
    '[class*="__toolbar"]',
    '[class*="__card"]',
    '[class*="__row"]',
    '[class*="__item"]',
    '[class*="__panel"]',
    '[class*="__modal"]',
    '[class*="__content"]',
].join(',');

const EDITABLE_SELECTOR = [
    'input:not([type="button"]):not([type="submit"]):not([type="reset"])',
    'select',
    'textarea',
    '[contenteditable="true"]',
    '[role="textbox"]',
    '[role="combobox"]',
    '[role="spinbutton"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[role="switch"]',
].join(',');

const BLOCK_SEMANTIC_SELECTOR = [
    '[role="dialog"]',
    'form',
    'details',
    'article',
    '[class*="__editor"]',
    '[class*="__form"]',
    '[class*="__card"]',
    '[class*="__item"]',
    '[class*="__row"]',
    '[class*="__group"]',
    '[class*="__section"]',
    '[class*="__field"]',
    '[class*="__control"]',
    '[class*="__setting"]',
].join(',');


/*
 * Только действительно опасные/побочные действия. Чтение, фильтры,
 * сортировки, расчёт-предпросмотр, раскрытие секций и открытие модалок
 * должны выполняться на втором экране тоже.
 */
const SAFE_LOCAL_ACTION_WORDS = /(?:(?:удалить|убрать)\s+(?:размер|ставк|правил|исключени|диапазон|строк|вариант|вид гранита|коэффициент)|сбросить|по умолчанию)/i;
const MUTATION_WORDS = /(?:сохран|импорт|применить новые|применить безопасный импорт|удалить материал|удалить поставщика|перенест|вернуть в очередь|опубликов|отправить|создать товар|создать формы|инициализ|объединить|очистить баз|загрузить файл)/i;
const DOWNLOAD_WORDS = /(?:экспорт|скачать|выгрузить)/i;
const SERVER_ACTION_DATA = new Set(['server', 'mutation', 'dangerous']);
const COMPONENT_STATE_NAMESPACES = new Set([
    'pricing-policy-preview-v1',
]);

let activeRuntime = null;
let orbitPatched = false;
let originalOrbitUpdate = null;
let originalOrbitDispatchEvent = null;

const now = () => Date.now();
const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));
const round = (value, precision = 5) => Number((Number(value) || 0).toFixed(precision));

const hashString = (value) => {
    let hash = 2166136261;
    const text = String(value || '');

    for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(36);
};

const resolvePageKey = () => {
    if (typeof window === 'undefined') return '';
    const pathname = String(window.location.pathname || '');

    return Object.entries(PAGE_CONFIGS)
        .find(([, config]) => config.match(pathname))?.[0] || '';
};

const getPageConfig = (pageKey) => PAGE_CONFIGS[pageKey] || null;

const safeCssEscape = (value) => {
    const text = String(value || '');
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(text);
    }
    return text.replace(/["\\]/g, '\\$&');
};

const normalizeText = (value, max = 180) => String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);

const getElementText = (element) => normalizeText(
    element?.getAttribute?.('aria-label')
    || element?.getAttribute?.('title')
    || element?.textContent
    || '',
    180
);

const cleanClassNames = (element) => Array.from(element?.classList || [])
    .filter((name) => (
        !name.startsWith('is-')
        && !name.startsWith('has-')
        && !name.startsWith('Mui-focused')
        && !name.startsWith('Mui-active')
        && !/^jss\d+$/.test(name)
        && name !== 'active'
    ))
    .slice(0, 4)
    .join('.');

const getNthOfType = (element) => {
    const parent = element?.parentElement;
    if (!parent) return 1;

    const siblings = Array.from(parent.children).filter(
        (item) => item.tagName === element.tagName
    );

    return Math.max(1, siblings.indexOf(element) + 1);
};

const isExcluded = (element) => (
    element instanceof Element
    && (element.matches(EXCLUDE_SELECTOR) || Boolean(element.closest(EXCLUDE_SELECTOR)))
);

const isInsidePage = (element, pageKey) => {
    if (!(element instanceof Element) || !pageKey || isExcluded(element)) return false;
    const config = getPageConfig(pageKey);
    if (!config) return false;

    return config.roots.some((selector) => {
        try {
            return Boolean(element.closest(selector));
        } catch (_error) {
            return false;
        }
    });
};

const findRoot = (element, pageKey) => {
    const config = getPageConfig(pageKey);
    if (!(element instanceof Element) || !config) return null;

    for (const selector of config.roots) {
        try {
            const root = element.closest(selector);
            if (root) return root;
        } catch (_error) {
            // ignore invalid selector in older browsers
        }
    }

    return null;
};

const getScopeElement = (element) => (
    element?.closest?.([
        '[data-admin-sync-key]',
        '[data-admin-sync-scope]',
        '[data-catalog-sync-scope]',
        '[data-admin-realtime-root]',
    ].join(',')) || null
);

const getScopeKey = (element) => {
    const scope = getScopeElement(element);
    if (!scope) return '';

    return normalizeText(
        scope.getAttribute('data-admin-sync-key')
        || scope.getAttribute('data-admin-sync-scope')
        || scope.getAttribute('data-catalog-sync-scope')
        || scope.getAttribute('data-admin-realtime-root')
        || '',
        160
    );
};


const getAdmin3DControlSemanticKey = (element) => {
    if (!(element instanceof Element)) return '';

    const root = element.closest(ADMIN_3D_ROOT_SELECTOR);
    if (!(root instanceof Element)) return '';

    const text = getElementText(element).toLowerCase();
    const scope = normalizeText(
        root.getAttribute('data-catalog-sync-scope') || 'admin-3d',
        160
    );

    if (element.matches('select') && element.closest('.adminProduct3DPreview__nav')) {
        return `${scope}:nav-category`;
    }

    if (element.matches(EDITABLE_SELECTOR)) {
        const host = element.closest([
            'label',
            '[class*="__field"]',
            '[class*="__control"]',
            '[class*="__switchRow"]',
            '[class*="__input"]',
            '[class*="__row"]',
            '[class*="__card"]',
            '[class*="__section"]',
        ].join(',')) || element.parentElement || root;

        const contextClass = Array.from(host?.classList || [])
            .find((name) => name.startsWith('adminProduct3DPreview__'))
            || Array.from(host?.parentElement?.classList || [])
                .find((name) => name.startsWith('adminProduct3DPreview__'))
            || 'field';

        const label = normalizeText(
            getLabelText(element)
            || element.getAttribute('aria-label')
            || element.getAttribute('placeholder')
            || element.getAttribute('name')
            || element.getAttribute('type')
            || element.tagName,
            100
        ).toLowerCase().replace(/\s+/g, '-');

        const scopeHost = element.closest(
            '[class*="__card"], [class*="__section"], [class*="__group"], details, form'
        ) || host.parentElement || root;

        const peers = Array.from(scopeHost.querySelectorAll(EDITABLE_SELECTOR))
            .filter((node) => {
                const nodeHost = node.closest([
                    'label',
                    '[class*="__field"]',
                    '[class*="__control"]',
                    '[class*="__switchRow"]',
                    '[class*="__input"]',
                    '[class*="__row"]',
                    '[class*="__card"]',
                    '[class*="__section"]',
                ].join(',')) || node.parentElement;

                const nodeContext = Array.from(nodeHost?.classList || [])
                    .find((name) => name.startsWith('adminProduct3DPreview__'))
                    || Array.from(nodeHost?.parentElement?.classList || [])
                        .find((name) => name.startsWith('adminProduct3DPreview__'))
                    || 'field';

                const nodeLabel = normalizeText(
                    getLabelText(node)
                    || node.getAttribute('aria-label')
                    || node.getAttribute('placeholder')
                    || node.getAttribute('name')
                    || node.getAttribute('type')
                    || node.tagName,
                    100
                ).toLowerCase().replace(/\s+/g, '-');

                return nodeContext === contextClass && nodeLabel === label;
            });

        const ordinal = Math.max(1, peers.indexOf(element) + 1);
        return `${scope}:field:${contextClass}:${label || 'control'}:${ordinal}`;
    }

    const pager = element.closest('.adminProduct3DPreview__pager');
    if (pager && element.matches('button,[role="button"]')) {
        const buttons = Array.from(pager.querySelectorAll('button,[role="button"]'));
        const index = Math.max(0, buttons.indexOf(element));
        return `${scope}:pager:${index === 0 ? 'prev' : 'next'}`;
    }

    if (element.closest('.adminProduct3DPreview__tabs') && element.matches('button,[role="tab"],[role="button"]')) {
        const label = normalizeText(text || getLabelText(element) || 'tab', 80)
            .replace(/\s+/g, '-');
        return `${scope}:tab:${label}`;
    }

    if (element.closest(ADMIN_3D_CONFIRM_SELECTOR) && element.matches('button,[role="button"]')) {
        let action = normalizeText(text, 100);
        if (/перейти без сохранения/i.test(action)) action = 'discard-and-follow';
        else if (/остаться здесь/i.test(action)) action = 'stay';
        else if (/сохранить и перейти/i.test(action)) action = 'save-and-follow';
        return `${scope}:confirm:${action.replace(/\s+/g, '-')}`;
    }

    if (element.closest('.adminProduct3DPreview__actions') && element.matches('button,[role="button"]')) {
        if (/сбросить/i.test(text)) return `${scope}:actions:reset`;
        if (/сохран/i.test(text)) return `${scope}:actions:save`;
    }

    if (element.closest('.adminProduct3DPreview__viewModes') && element.matches('button,[role="button"]')) {
        const label = normalizeText(text || 'mode', 80).replace(/\s+/g, '-');
        return `${scope}:view-mode:${label}`;
    }

    if (element.matches('button,[role="button"]')) {
        const aria = normalizeText(
            element.getAttribute('aria-label')
            || element.getAttribute('title')
            || '',
            100
        ).toLowerCase();

        if (aria) return `${scope}:button:${aria.replace(/\s+/g, '-')}`;

        const stableText = normalizeText(text, 100)
            .replace(/\s+/g, '-');

        if (stableText) {
            const context = Array.from(
                element.parentElement?.classList || []
            ).find((name) => name.startsWith('adminProduct3DPreview__')) || 'controls';

            return `${scope}:button:${context}:${stableText}`;
        }
    }

    return '';
};

const getExplicitKey = (element) => {
    if (element?.matches?.('.app-header__collapseToggle')) {
        return 'admin-header-collapse-toggle';
    }

    const admin3DKey = getAdmin3DControlSemanticKey(element);
    if (admin3DKey) return admin3DKey;

    return normalizeText(
        element?.getAttribute?.('data-admin-sync-id')
        || element?.getAttribute?.('data-admin-sync-key')
        || element?.getAttribute?.('data-catalog-sync-key')
        || element?.getAttribute?.('data-catalog-scroll-id')
        || element?.id
        || element?.getAttribute?.('name')
        || '',
        180
    );
};

const getStablePath = (element, pageKey) => {
    const root = findRoot(element, pageKey);
    if (!root) return '';

    const explicit = getExplicitKey(element);
    const scopeKey = getScopeKey(element);

    if (explicit) {
        return [
            pageKey,
            scopeKey || 'root',
            String(element.tagName || '').toLowerCase(),
            explicit,
        ].join('|');
    }

    const segments = [];
    let current = element;

    while (current && current !== root && segments.length < 10) {
        const className = cleanClassNames(current);
        const role = normalizeText(current.getAttribute?.('role') || '', 50);
        const type = normalizeText(current.getAttribute?.('type') || '', 50);
        const name = normalizeText(current.getAttribute?.('name') || '', 80);

        segments.unshift([
            String(current.tagName || '').toLowerCase(),
            className ? `.${className}` : '',
            role ? `[role=${role}]` : '',
            type ? `[type=${type}]` : '',
            name ? `[name=${name}]` : '',
            `:${getNthOfType(current)}`,
        ].join(''));

        current = current.parentElement;
    }

    const rootName = normalizeText(
        root.getAttribute?.('data-admin-realtime-root')
        || root.getAttribute?.('data-admin-sync-key')
        || root.getAttribute?.('data-admin-sync-scope')
        || root.getAttribute?.('data-catalog-sync-scope')
        || cleanClassNames(root)
        || root.tagName
        || 'root',
        180
    );

    /* ВАЖНО: текст кнопки сюда намеренно НЕ входит. Он часто меняется
       "Развернуть/Свернуть", "Показать/Скрыть", "Сохранить/Сохранение…". */
    return [pageKey, scopeKey || rootName, segments.join('>')].join('|');
};

const ensureTargetId = (element, pageKey) => {
    if (!(element instanceof Element)) return '';

    const path = getStablePath(element, pageKey);
    if (!path) return '';

    const signature = hashString(path);
    const owner = element.getAttribute(OWNER_ATTR);
    const existing = element.getAttribute(TARGET_ATTR);
    const existingSignature = element.getAttribute(TARGET_SIGNATURE_ATTR);

    if (
        existing
        && owner === pageKey
        && existingSignature === signature
    ) {
        return existing;
    }

    const id = `ui-${signature}`;
    element.setAttribute(TARGET_ATTR, id);
    element.setAttribute(OWNER_ATTR, pageKey);
    element.setAttribute(TARGET_SIGNATURE_ATTR, signature);
    return id;
};

const getOrdinal = (element, root) => {
    if (!(element instanceof Element) || !(root instanceof Element)) return 0;

    const tag = String(element.tagName || '').toLowerCase();
    const role = normalizeText(element.getAttribute('role') || '', 50);
    const type = normalizeText(element.getAttribute('type') || '', 50);
    const name = normalizeText(element.getAttribute('name') || '', 80);

    const candidates = Array.from(root.querySelectorAll(tag || '*')).filter((item) => {
        if (role && item.getAttribute('role') !== role) return false;
        if (type && item.getAttribute('type') !== type) return false;
        if (name && item.getAttribute('name') !== name) return false;
        return true;
    });

    return Math.max(0, candidates.indexOf(element));
};

const getLabelText = (element) => {
    if (!(element instanceof Element)) return '';

    const direct = normalizeText(
        element.getAttribute('aria-label')
        || element.getAttribute('title')
        || element.getAttribute('placeholder')
        || '',
        180
    );
    if (direct) return direct;

    if (
        (element instanceof HTMLInputElement
            || element instanceof HTMLSelectElement
            || element instanceof HTMLTextAreaElement)
        && element.labels?.length
    ) {
        const text = normalizeText(
            Array.from(element.labels).map((label) => label.textContent || '').join(' '),
            180
        );
        if (text) return text;
    }

    const wrappingLabel = element.closest('label');
    if (wrappingLabel) {
        const text = normalizeText(wrappingLabel.textContent || '', 180);
        if (text) return text;
    }

    const parentLabel = element.parentElement?.querySelector?.(':scope > label');
    return normalizeText(parentLabel?.textContent || '', 180);
};

const getDescriptorClassTokens = (element) => Array.from(element?.classList || [])
    .filter((name) => (
        name
        && !name.startsWith('is-')
        && !name.startsWith('has-')
        && !name.startsWith('Mui-focused')
        && !name.startsWith('Mui-active')
        && !/^jss\d+$/.test(name)
        && name !== 'active'
    ))
    .slice(0, 8);

const getContextText = (element, pageKey) => {
    if (!(element instanceof Element)) return '';
    const root = findRoot(element, pageKey);
    const surface = element.closest(CURSOR_SURFACE_SELECTOR) || root;
    if (!(surface instanceof Element)) return '';

    const heading = surface.querySelector?.(
        ':scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > legend, :scope > summary, :scope > header strong, :scope > strong'
    );
    const headingText = normalizeText(heading?.textContent || '', 150);
    if (headingText) return headingText;

    const ownLabel = getLabelText(element);
    if (ownLabel) return ownLabel;

    return normalizeText(surface.getAttribute('aria-label') || '', 150);
};

const getSemanticDataKey = (element) => normalizeText(
    element?.getAttribute?.('data-admin-sync-id')
    || element?.getAttribute?.('data-admin-sync-key')
    || element?.getAttribute?.('data-admin-sync-scope')
    || element?.getAttribute?.('data-catalog-sync-key')
    || element?.getAttribute?.('data-catalog-sync-scope')
    || element?.getAttribute?.('data-catalog-scroll-id')
    || element?.getAttribute?.('data-testid')
    || element?.getAttribute?.('data-test-id')
    || '',
    180
);

const buildDescriptor = (element, pageKey) => {
    if (!(element instanceof Element)) return null;
    const root = findRoot(element, pageKey);
    if (!root) return null;

    const scopeRoot = getScopeElement(element) || root;
    const href = element instanceof HTMLAnchorElement
        ? normalizeText(element.getAttribute('href') || '', 240)
        : '';

    return {
        explicit: getExplicitKey(element),
        semanticKey: getSemanticDataKey(element),
        scope: getScopeKey(element),
        root: normalizeText(
            root.getAttribute?.('data-admin-realtime-root')
            || root.getAttribute?.('data-admin-sync-key')
            || root.getAttribute?.('data-admin-sync-scope')
            || root.getAttribute?.('data-catalog-sync-scope')
            || cleanClassNames(root)
            || root.tagName,
            180
        ),
        tag: String(element.tagName || '').toLowerCase(),
        role: normalizeText(element.getAttribute('role') || '', 50),
        type: normalizeText(element.getAttribute('type') || '', 50),
        name: normalizeText(element.getAttribute('name') || '', 100),
        ariaLabel: normalizeText(element.getAttribute('aria-label') || '', 180),
        title: normalizeText(element.getAttribute('title') || '', 180),
        placeholder: normalizeText(element.getAttribute('placeholder') || '', 180),
        href,
        labelText: getLabelText(element),
        contextText: getContextText(element, pageKey),
        text: getElementText(element),
        classTokens: getDescriptorClassTokens(element),
        ordinal: getOrdinal(element, root),
        scopeOrdinal: getOrdinal(element, scopeRoot),
    };
};

const findByExplicit = (descriptor, pageKey) => {
    const explicit = normalizeText(descriptor?.explicit || '', 180);
    if (!explicit) return null;

    const escaped = safeCssEscape(explicit);
    const selectors = [
        `[data-admin-sync-id="${escaped}"]`,
        `[data-admin-sync-key="${escaped}"]`,
        `[data-catalog-sync-key="${escaped}"]`,
        `[data-catalog-scroll-id="${escaped}"]`,
        `#${escaped}`,
        `[name="${escaped}"]`,
    ];

    for (const selector of selectors) {
        try {
            const nodes = Array.from(document.querySelectorAll(selector));
            const found = nodes.find((node) => isInsidePage(node, pageKey));
            if (found) return found;
        } catch (_error) {
            // continue
        }
    }

    return null;
};

const getScopeRoot = (descriptor, pageKey) => {
    const scope = normalizeText(descriptor?.scope || '', 160);
    if (scope) {
        const escaped = safeCssEscape(scope);
        const selectors = [
            `[data-admin-sync-key="${escaped}"]`,
            `[data-admin-sync-scope="${escaped}"]`,
            `[data-catalog-sync-scope="${escaped}"]`,
            `[data-admin-realtime-root="${escaped}"]`,
        ];
        for (const selector of selectors) {
            try {
                const found = Array.from(document.querySelectorAll(selector))
                    .find((node) => isInsidePage(node, pageKey));
                if (found) return found;
            } catch (_error) {
                // continue
            }
        }
    }

    const config = getPageConfig(pageKey);
    if (!config) return document.body;
    for (const selector of config.roots) {
        try {
            const found = document.querySelector(selector);
            if (found) return found;
        } catch (_error) {
            // continue
        }
    }
    return document.body;
};

const tokenOverlapScore = (left = [], right = []) => {
    const a = new Set((Array.isArray(left) ? left : []).filter(Boolean));
    const b = new Set((Array.isArray(right) ? right : []).filter(Boolean));
    if (!a.size || !b.size) return 0;

    let matches = 0;
    a.forEach((token) => {
        if (b.has(token)) matches += 1;
    });

    return Math.min(36, matches * 9);
};

const scoreCandidate = (element, descriptor) => {
    if (!(element instanceof Element)) return -1000;
    let score = 0;

    const role = normalizeText(element.getAttribute('role') || '', 50);
    const type = normalizeText(element.getAttribute('type') || '', 50);
    const name = normalizeText(element.getAttribute('name') || '', 100);
    const ariaLabel = normalizeText(element.getAttribute('aria-label') || '', 180);
    const title = normalizeText(element.getAttribute('title') || '', 180);
    const placeholder = normalizeText(element.getAttribute('placeholder') || '', 180);
    const href = element instanceof HTMLAnchorElement
        ? normalizeText(element.getAttribute('href') || '', 240)
        : '';
    const text = getElementText(element);
    const labelText = getLabelText(element);
    const contextText = getContextText(element, activeRuntime?.pageKey || resolvePageKey());
    const semanticKey = getSemanticDataKey(element);
    const classes = getDescriptorClassTokens(element);

    if (descriptor.tag && String(element.tagName || '').toLowerCase() === descriptor.tag) score += 7;
    if (descriptor.role && role === descriptor.role) score += 18;
    if (descriptor.type && type === descriptor.type) score += 16;
    if (descriptor.name && name === descriptor.name) score += 58;
    if (descriptor.ariaLabel && ariaLabel === descriptor.ariaLabel) score += 82;
    if (descriptor.title && title === descriptor.title) score += 36;
    if (descriptor.placeholder && placeholder === descriptor.placeholder) score += 64;
    if (descriptor.href && href === descriptor.href) score += 68;
    if (descriptor.labelText && labelText === descriptor.labelText) score += 72;
    if (descriptor.contextText && contextText === descriptor.contextText) score += 46;

    if (descriptor.text && text === descriptor.text) score += 52;
    else if (
        descriptor.text
        && text
        && descriptor.text.length >= 4
        && (text.includes(descriptor.text) || descriptor.text.includes(text))
    ) score += 18;

    if (descriptor.semanticKey && semanticKey === descriptor.semanticKey) score += 150;
    if (descriptor.explicit && getExplicitKey(element) === descriptor.explicit) score += 180;
    if (descriptor.scope && getScopeKey(element) === descriptor.scope) score += 34;

    score += tokenOverlapScore(descriptor.classTokens, classes);
    return score;
};

const findByDescriptorDetailed = (descriptor, pageKey) => {
    if (!descriptor || typeof descriptor !== 'object') {
        return { node: null, score: -1000, exact: false };
    }

    const explicit = findByExplicit(descriptor, pageKey);
    if (explicit) {
        return { node: explicit, score: 1000, exact: true };
    }

    const root = getScopeRoot(descriptor, pageKey);
    const tag = /^[a-z0-9-]+$/i.test(String(descriptor.tag || ''))
        ? String(descriptor.tag)
        : '*';

    let candidates = [];
    try {
        candidates = Array.from(root.querySelectorAll(tag));
    } catch (_error) {
        candidates = Array.from(root.querySelectorAll('*'));
    }

    if (root.matches?.(tag)) candidates.unshift(root);
    candidates = [...new Set(candidates)].filter((node) => isInsidePage(node, pageKey));
    if (!candidates.length) return { node: null, score: -1000, exact: false };

    const role = normalizeText(descriptor.role || '', 50);
    const type = normalizeText(descriptor.type || '', 50);
    const name = normalizeText(descriptor.name || '', 100);
    const semanticKey = normalizeText(descriptor.semanticKey || '', 180);

    let pool = candidates.filter((node) => {
        if (semanticKey && getSemanticDataKey(node) === semanticKey) return true;
        if (name && node.getAttribute('name') !== name) return false;
        if (role && node.getAttribute('role') !== role) return false;
        if (type && node.getAttribute('type') !== type) return false;
        return true;
    });
    if (!pool.length) pool = candidates;

    const ranked = pool
        .map((node) => ({ node, score: scoreCandidate(node, descriptor) }))
        .sort((left, right) => right.score - left.score);

    const best = ranked[0] || null;
    const second = ranked[1] || null;
    if (!best) return { node: null, score: -1000, exact: false };

    /*
     * Не выбираем "первую похожую кнопку" при слабом совпадении. Именно это
     * раньше давало курсор на соседнем control при повторяющихся кнопках.
     * ordinal используется только как tie-breaker, но не как самостоятельное
     * доказательство совпадения при responsive DOM.
     */
    const scopeOrdinal = Math.max(0, Number(descriptor.scopeOrdinal) || 0);
    const ordinal = Math.max(0, Number(descriptor.ordinal) || 0);
    let selected = best;

    if (second && Math.abs(best.score - second.score) <= 6) {
        const scopeRoot = getScopeElement(best.node) || root;
        const scopedPool = pool.filter((node) => (getScopeElement(node) || root) === scopeRoot);
        selected = scopedPool[scopeOrdinal] || pool[ordinal] || best;
        selected = {
            node: selected.node || selected,
            score: scoreCandidate(selected.node || selected, descriptor),
        };
    }

    return {
        node: selected.node,
        score: selected.score,
        exact: false,
    };
};

const findByDescriptor = (descriptor, pageKey) => {
    const result = findByDescriptorDetailed(descriptor, pageKey);
    if (result.score >= 20) return result.node;
    return null;
};

const findTargetDetailed = (payload, pageKey) => {
    const targetId = String(payload?.targetId || '');
    if (targetId) {
        try {
            const node = document.querySelector(
                `[${TARGET_ATTR}="${safeCssEscape(targetId)}"]`
            );
            if (node && isInsidePage(node, pageKey)) {
                return { node, score: 1200, exact: true };
            }
        } catch (_error) {
            // descriptor fallback below
        }
    }

    return findByDescriptorDetailed(payload?.descriptor, pageKey);
};

const findTarget = (payload, pageKey) => {
    const result = findTargetDetailed(payload, pageKey);
    return result.score >= 20 ? result.node : null;
};

const nearestInteractive = (target, pageKey) => {
    if (!(target instanceof Element)) return null;
    const element = target.closest(INTERACTIVE_SELECTOR);
    return element && isInsidePage(element, pageKey) ? element : null;
};

const nearestClickable = (target, pageKey) => {
    if (!(target instanceof Element)) return null;
    const element = target.closest(CLICK_SELECTOR);
    return element && isInsidePage(element, pageKey) ? element : null;
};

const cursorRelativePoint = (clientX, clientY, element) => {
    if (!(element instanceof Element)) return null;
    const rect = element.getBoundingClientRect();
    if (!(rect.width > 0) || !(rect.height > 0)) return null;

    return {
        x: clamp((clientX - rect.left) / rect.width, 0, 1),
        y: clamp((clientY - rect.top) / rect.height, 0, 1),
    };
};

/*
 * V4 cursor anchors.
 *
 * Старый рабочий механизм в sales-session использовал один явный scope и
 * координату внутри него. Здесь делаем ту же идею многоуровневой:
 * control -> semantic surface -> explicit scope -> root.
 *
 * На принимающем устройстве НЕ выбираем слабое совпадение. Если точный
 * control не найден, переходим к более широкому якорю. Так responsive DOM
 * может быть другим, но курсор не перескакивает на соседнюю кнопку/строку.
 */
const getCursorSurfaceChain = (origin, pageKey) => {
    if (!(origin instanceof Element) || !isInsidePage(origin, pageKey)) return [];

    const root = findRoot(origin, pageKey);
    if (!(root instanceof Element)) return [];

    const chain = [];
    let current = origin;
    while (current && current instanceof Element) {
        if (current.matches?.(CURSOR_SURFACE_SELECTOR)) chain.push(current);
        if (current === root) break;
        current = current.parentElement;
    }

    if (!chain.includes(root)) chain.push(root);
    return chain;
};

const buildCursorAnchor = ({
    element,
    clientX,
    clientY,
    pageKey,
    kind,
}) => {
    if (!(element instanceof Element)) return null;
    const point = cursorRelativePoint(clientX, clientY, element);
    if (!point) return null;

    return {
        targetId: ensureTargetId(element, pageKey),
        descriptor: buildDescriptor(element, pageKey),
        x: round(point.x, 6),
        y: round(point.y, 6),
        kind: normalizeText(kind || 'surface', 30),
    };
};

const cursorAnchorSignature = (anchor = {}) => [
    anchor.kind || '',
    anchor.targetId || '',
    anchor.descriptor?.semanticKey || '',
    anchor.descriptor?.explicit || '',
    anchor.descriptor?.scope || '',
    anchor.descriptor?.name || '',
    anchor.descriptor?.ariaLabel || '',
    anchor.descriptor?.labelText || '',
    anchor.descriptor?.text || '',
].join('|');

const resolveCursorAnchorPayload = ({
    clientX = 0,
    clientY = 0,
    sourceElement = null,
    pageKey = '',
} = {}) => {
    const origin = sourceElement instanceof Element ? sourceElement : null;
    if (!(origin instanceof Element) || isExcluded(origin)) {
        return { anchors: [], primary: null, scope: null, primaryElement: null, scopeElement: null };
    }

    const root = findRoot(origin, pageKey);
    if (!(root instanceof Element)) {
        return { anchors: [], primary: null, scope: null, primaryElement: null, scopeElement: null };
    }

    // v19: курсор привязывается только к ЯВНО объявленным semantic surfaces.
    // Никакого fuzzy-поиска "похожей кнопки" для позиции курсора.
    const explicitSelector = [
        '[data-admin-sync-id]',
        '[data-admin-sync-key]',
        '[data-admin-sync-scope]',
        '[data-catalog-sync-key]',
        '[data-catalog-sync-scope]',
        '[data-catalog-scroll-id]',
        '[data-admin-realtime-root]',
    ].join(',');

    const candidates = [];
    let current = origin;
    while (current && current instanceof Element) {
        if (current.matches?.(explicitSelector) && isInsidePage(current, pageKey)) {
            candidates.push(current);
        }
        if (current === root) break;
        current = current.parentElement;
    }
    if (!candidates.includes(root)) candidates.push(root);

    const unique = [...new Set(candidates)].slice(0, CURSOR_ANCHOR_LIMIT);
    const anchors = unique.map((element, index) => buildCursorAnchor({
        element,
        clientX,
        clientY,
        pageKey,
        kind: index === unique.length - 1 ? 'root' : 'surface',
    })).filter(Boolean);

    return {
        anchors,
        primary: anchors[0] || null,
        scope: anchors[1] || anchors[0] || null,
        primaryElement: unique[0] || null,
        scopeElement: unique[1] || unique[0] || null,
    };
};

const nearestEditable = (target, pageKey) => {
    if (!(target instanceof Element)) return null;

    const direct = target.closest(EDITABLE_SELECTOR);
    if (direct && isInsidePage(direct, pageKey)) return direct;

    const label = target.closest('label');
    if (label) {
        const nested = label.querySelector(EDITABLE_SELECTOR);
        if (nested && isInsidePage(nested, pageKey)) return nested;
    }

    return null;
};

const getFieldVisualElement = (field) => {
    if (!(field instanceof Element)) return null;

    if (
        field.matches('[role="checkbox"],[role="radio"],[role="switch"]')
        || (field instanceof HTMLInputElement && ['checkbox', 'radio'].includes(field.type))
    ) {
        return field.closest(
            'label, [class*="__switchRow"], [class*="__checkbox"], [class*="__radio"], [class*="__toggle"]'
        ) || field;
    }

    return field;
};

const getFieldLabel = (field, pageKey) => {
    if (!(field instanceof Element)) return 'Поле';

    return normalizeText(
        getLabelText(field)
        || field.getAttribute('aria-label')
        || field.getAttribute('placeholder')
        || getContextText(field, pageKey)
        || field.getAttribute('name')
        || 'Поле',
        120
    );
};

const injectStyles = () => {
    if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
        [${TARGET_ATTR}][data-admin-remote-hover="true"] {
            outline: 2px solid var(--admin-remote-color, #7b8cff) !important;
            outline-offset: 2px !important;
            box-shadow: 0 0 0 4px rgba(123, 140, 255, .16) !important;
        }
        [${TARGET_ATTR}][data-admin-remote-pressed="true"] {
            filter: brightness(1.10) !important;
        }
        [${TARGET_ATTR}][data-admin-remote-focus="true"] {
            outline: 3px solid var(--admin-remote-color, #7b8cff) !important;
            outline-offset: 3px !important;
        }
        [${TARGET_ATTR}][data-admin-remote-loading="true"] {
            box-shadow: inset 0 0 0 2px var(--admin-remote-color, #7b8cff) !important;
        }
        .adminRealtimeUiCursorLayer {
            position: fixed;
            inset: 0;
            z-index: 2147483500;
            pointer-events: none;
            overflow: hidden;
        }
        .adminRealtimeUiCursor {
            position: absolute;
            left: 0;
            top: 0;
            transform: translate3d(-100px, -100px, 0);
            will-change: transform;
            transition: opacity 120ms ease;
            opacity: 0;
        }
        .adminRealtimeUiCursor.is-visible { opacity: 1; }
        .adminRealtimeUiCursor__arrow {
            width: 20px;
            height: 26px;
            display: block;
            filter: drop-shadow(0 2px 4px rgba(0, 0, 0, .45));
        }
        .adminRealtimeUiCursor__arrow path {
            fill: var(--admin-remote-color, #7b8cff);
            stroke: rgba(255,255,255,.92);
            stroke-width: 1.25px;
        }
        .adminRealtimeUiCursor__label {
            display: inline-block;
            margin: -2px 0 0 15px;
            max-width: 220px;
            padding: 4px 7px;
            border-radius: 7px;
            background: var(--admin-remote-color, #7b8cff);
            color: #fff;
            box-shadow: 0 5px 16px rgba(0,0,0,.28);
            font: 700 11px/1.2 Arial, sans-serif;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .adminRealtimeUiFieldLockLayer {
            position: fixed;
            inset: 0;
            z-index: 2147483450;
            pointer-events: none;
            overflow: visible;
        }
        .adminRealtimeUiFieldLockFrame {
            position: absolute;
            left: 0;
            top: 0;
            border: 2px solid var(--admin-lock-color, #f59e0b);
            border-radius: 10px;
            box-shadow:
                0 0 0 3px rgba(245, 158, 11, .16),
                0 6px 22px rgba(0,0,0,.16);
            transform: translate3d(-1000px,-1000px,0);
            will-change: transform, width, height;
        }
        .adminRealtimeUiFieldLockBadge {
            position: absolute;
            left: 0;
            top: 0;
            max-width: min(340px, calc(100vw - 16px));
            padding: 4px 8px 5px;
            border-radius: 7px;
            background: var(--admin-lock-color, #f59e0b);
            color: #fff;
            box-shadow: 0 7px 22px rgba(0,0,0,.30);
            font: 700 11px/1.25 Arial, sans-serif;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            transform: translate3d(-1000px,-1000px,0);
        }
        .adminRealtimeUiSelectLayer {
            position: fixed;
            inset: 0;
            z-index: 2147483475;
            pointer-events: none;
            overflow: visible;
        }
        .adminRealtimeUiSelectMirror {
            position: absolute;
            left: 0;
            top: 0;
            min-width: 180px;
            max-width: min(420px, calc(100vw - 16px));
            max-height: min(320px, calc(100vh - 16px));
            overflow: hidden;
            border: 1px solid color-mix(in srgb, var(--admin-remote-color, #7b8cff) 78%, #ffffff 22%);
            border-radius: 9px;
            background: var(--admin-surface, #fff);
            color: var(--admin-text, #111827);
            box-shadow: 0 14px 34px rgba(0, 0, 0, .28), 0 0 0 2px rgba(123, 140, 255, .12);
            transform: translate3d(-1000px, -1000px, 0);
            font: 500 13px/1.3 Arial, sans-serif;
        }
        .adminRealtimeUiSelectMirror__head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            padding: 6px 9px;
            border-bottom: 1px solid rgba(127, 127, 127, .20);
            color: var(--admin-remote-color, #7b8cff);
            font-size: 11px;
            font-weight: 800;
        }
        .adminRealtimeUiSelectMirror__current {
            padding: 6px 9px;
            border-bottom: 1px solid rgba(127, 127, 127, .14);
            background: color-mix(in srgb, var(--admin-remote-color, #7b8cff) 8%, transparent);
            font-size: 11px;
            font-weight: 700;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .adminRealtimeUiSelectMirror__options {
            max-height: 270px;
            overflow: hidden;
            padding: 4px;
        }
        .adminRealtimeUiSelectMirror__option {
            display: block;
            padding: 7px 9px;
            border-radius: 6px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .adminRealtimeUiSelectMirror__option.is-selected {
            background: color-mix(in srgb, var(--admin-remote-color, #7b8cff) 18%, transparent);
            color: var(--admin-remote-color, #7b8cff);
            font-weight: 800;
        }
        .adminRealtimeUiActivityLayer {
            position: fixed;
            z-index: 2147483400;
            top: 76px;
            right: 18px;
            display: grid;
            gap: 7px;
            width: min(360px, calc(100vw - 36px));
            pointer-events: none;
        }
        .adminRealtimeUiActivity {
            padding: 9px 11px;
            border-radius: 11px;
            border: 1px solid var(--admin-remote-color, #7b8cff);
            background: rgba(9, 13, 24, .94);
            color: #fff;
            box-shadow: 0 12px 32px rgba(0, 0, 0, .32);
            font: 700 12px/1.35 Arial, sans-serif;
        }
        .adminRealtimeUiActivity span {
            display: block;
            margin-top: 2px;
            color: rgba(255, 255, 255, .68);
            font-weight: 500;
        }
    `;

    document.head.appendChild(style);
};

const colorForSocket = (socketId) => {
    const hue = parseInt(hashString(socketId || 'remote'), 36) % 360;
    return `hsl(${hue} 78% 62%)`;
};

const patchOrbitControls = () => {
    if (orbitPatched) return;
    orbitPatched = true;

    originalOrbitUpdate = OrbitControls.prototype.update;
    originalOrbitDispatchEvent = OrbitControls.prototype.dispatchEvent;

    OrbitControls.prototype.update = function patchedAdminRealtimeUpdate(...args) {
        activeRuntime?.registerControls(this);
        return originalOrbitUpdate.apply(this, args);
    };

    OrbitControls.prototype.dispatchEvent = function patchedAdminRealtimeDispatch(event) {
        const result = originalOrbitDispatchEvent.call(this, event);
        activeRuntime?.handleOrbitEvent(this, event);
        return result;
    };
};

class AdminRealtimeUiRuntime {
    constructor({ getEnabled, getPresenceMeta, onStatusChange }) {
        this.socket = getSocket();
        this.getEnabled = typeof getEnabled === 'function' ? getEnabled : () => true;
        this.getPresenceMeta = typeof getPresenceMeta === 'function'
            ? getPresenceMeta
            : () => ({});
        this.onStatusChange = typeof onStatusChange === 'function'
            ? onStatusChange
            : () => {};

        this.pageKey = '';
        this.joinedPageKey = '';
        this.joining = false;
        this.users = [];
        this.applyingRemote = false;
        this.suppressUntil = 0;
        this.destroyed = false;
        this.sequence = 0;
        this.valueTimers = new Map();
        this.lastHoverId = '';
        this.lastPressedId = '';
        this.lastFocusId = '';
        this.lastCursorAt = 0;
        this.lastScrollAt = new Map();
        // v15: никаких массивов отложенных DOM-событий. Для stateful UI
        // хранится только ПОСЛЕДНЕЕ authoritative-состояние каждого ключа.
        // Если target ещё не смонтирован, ключ отмечается pending и будет
        // применён один раз после появления нужного DOM/OrbitControls.
        this.authoritativePendingKeys = new Map();
        this.controlsByTarget = new Map();
        this.lastCameraAt = new WeakMap();
        this.remoteCameraApplying = false;
        this.cursorLayer = null;
        /*
         * Cursor visual identity = человек, а не socket.
         * Две вкладки одного userId остаются двумя socket-подключениями, но
         * на экране существует ровно один курсор этого пользователя.
         */
        this.cursorNodes = new Map();
        this.cursorTimers = new Map();
        this.remoteCursorPayloads = new Map();
        this.remoteCursorSequences = new Map(); // sequence остаётся per socket
        this.cursorAnchorCache = new Map();
        this.cursorMotion = new Map();
        this.cursorOwnerSocketByIdentity = new Map();
        this.cursorIdentityBySocket = new Map();
        this.cursorAnimationFrame = null;
        this.cursorReflowFrame = null;
        this.activityLayer = null;
        this.selectMirrorLayer = null;
        this.remoteSelectMirrors = new Map();
        this.remoteSelectPayloads = new Map();
        this.remoteSelectTimers = new Map();
        this.localOpenSelect = null;
        this.localSelectCursorHeartbeat = null;
        this.localSelectCursorAnchor = null;
        this.blockLocks = new Map();
        this.ownedBlockIds = new Set();
        this.pendingBlockIds = new Set();
        this.lockElements = new Map();
        this.lockBadgeLayer = null;
        this.lockFrames = new Map();
        this.lockBadges = new Map();
        this.lockReleaseTimers = new Map();
        this.lockReflowFrame = null;
        this.lastBlockNoticeAt = new Map();
        this.lastAdmin3DStateSignature = '';
        this.admin3DApplyTimer = null;
        this.admin3DSyncTimer = null;

        // Последние server-authoritative UI states этой страницы.
        // Нужны для reconnect и для DOM, который появился позже события.
        this.authoritativeEvents = new Map();
        this.authoritativeRevisions = new Map();
        this.authoritativeReplayFrame = null;
        this.lastRemoteApplyMs = 0;
        this.remoteApplySamples = [];

        this.lastUiUserCount = 0;
        this.lastPresenceMetaSignature = '';
        this.observer = null;
        this.routeTimer = null;

        injectStyles();
        patchOrbitControls();
        this.bind();
        this.syncRouteAndConnection();
        this.routeTimer = window.setInterval(
            () => this.syncRouteAndConnection(),
            ROUTE_CHECK_MS
        );
        this.blockHeartbeatTimer = window.setInterval(
            () => this.heartbeatOwnedBlocks(),
            BLOCK_LOCK_HEARTBEAT_MS
        );
    }

    get config() {
        return getPageConfig(this.pageKey);
    }

    isEnabled() {
        return Boolean(this.pageKey && this.getEnabled(this.pageKey));
    }

    canEmit() {
        return Boolean(
            !this.destroyed
            && !this.applyingRemote
            && now() >= this.suppressUntil
            && this.isEnabled()
            && this.socket?.connected
            && this.joinedPageKey === this.pageKey
        );
    }

    getPresenceMetadata() {
        const raw = this.getPresenceMeta(this.pageKey) || {};
        const focusNumber = raw.focusNumber;

        return {
            focusType: normalizeText(raw.focusType || '', 80),
            focusNumber: focusNumber === null || focusNumber === undefined || focusNumber === ''
                ? null
                : (Number.isFinite(Number(focusNumber)) ? Number(focusNumber) : normalizeText(focusNumber, 120)),
            focusKey: normalizeText(raw.focusKey || '', 160),
            focusLabel: normalizeText(raw.focusLabel || '', 180),
        };
    }

    syncPresenceMetadata(force = false) {
        if (
            !this.pageKey
            || !this.isEnabled()
            || !this.socket?.connected
            || this.joinedPageKey !== this.pageKey
        ) {
            return;
        }

        const metadata = this.getPresenceMetadata();
        const signature = JSON.stringify(metadata);
        if (!force && signature === this.lastPresenceMetaSignature) return;

        this.lastPresenceMetaSignature = signature;
        this.socket.emit('adminRealtime:presenceMeta', {
            token: window.localStorage.getItem('token'),
            pageKey: this.pageKey,
            ...metadata,
        });
    }

    recordRemoteApplyDuration(value) {
        const duration = Number(value);
        if (!Number.isFinite(duration) || duration < 0) return;

        this.lastRemoteApplyMs = duration;
        this.remoteApplySamples.push(duration);
        if (this.remoteApplySamples.length > 24) {
            this.remoteApplySamples.splice(0, this.remoteApplySamples.length - 24);
        }
    }

    getDiagnosticsSnapshot() {
        this.pruneAuthoritativePending();
        const revisions = Array.from(this.authoritativeRevisions.values())
            .map((value) => Number(value) || 0)
            .filter((value) => value > 0);
        const localRevision = revisions.length ? Math.max(...revisions) : 0;
        const averageApplyMs = this.remoteApplySamples.length
            ? this.remoteApplySamples.reduce((sum, value) => sum + value, 0)
                / this.remoteApplySamples.length
            : 0;

        return {
            pageKey: this.pageKey,
            joinedPageKey: this.joinedPageKey,
            localRevision,
            // Q теперь отражает число уникальных state-ключей, которые
            // реально ждут появления target. Оно не может расти от старых
            // camera/scroll/click событий тысячами.
            pendingQueue: this.authoritativePendingKeys.size,
            applyMs: Math.round(averageApplyMs * 10) / 10,
            lastApplyMs: Math.round(Number(this.lastRemoteApplyMs || 0) * 10) / 10,
            authoritativeStates: this.authoritativeEvents.size,
            connected: Boolean(
                this.pageKey
                && this.isEnabled()
                && this.socket?.connected
                && this.joinedPageKey === this.pageKey
            ),
        };
    }

    reportStatus(extra = {}) {
        this.onStatusChange({
            pageKey: this.pageKey,
            connected: Boolean(
                this.pageKey
                && this.isEnabled()
                && this.socket?.connected
                && this.joinedPageKey === this.pageKey
            ),
            users: this.users,
            socketId: this.socket?.id || '',
            lockedFieldsCount: Array.from(this.blockLocks.values())
                .filter((lock) => lock.socketId !== this.socket?.id).length,
            ...extra,
        });
    }

    bind() {
        this.onConnect = this.onConnect.bind(this);
        this.onDisconnect = this.onDisconnect.bind(this);
        this.onUiUsers = this.onUiUsers.bind(this);
        this.onUiLocks = this.onUiLocks.bind(this);
        this.onRemoteUiState = this.onRemoteUiState.bind(this);
        this.onUiSnapshot = this.onUiSnapshot.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerOver = this.onPointerOver.bind(this);
        this.onPointerOut = this.onPointerOut.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onPointerCancel = this.onPointerUp.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onBeforeInput = this.onBeforeInput.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onToggle = this.onToggle.bind(this);
        this.onDocumentScroll = this.onDocumentScroll.bind(this);
        this.onWindowScroll = this.onWindowScroll.bind(this);
        this.onCursorViewportChange = this.onCursorViewportChange.bind(this);

        this.socket?.on('connect', this.onConnect);
        this.socket?.on('disconnect', this.onDisconnect);
        this.socket?.on('adminRealtime:users', this.onUiUsers);
        this.socket?.on('adminRealtime:locks', this.onUiLocks);
        this.socket?.on('adminRealtime:state', this.onRemoteUiState);
        this.socket?.on('adminRealtime:snapshot', this.onUiSnapshot);

        document.addEventListener('pointermove', this.onPointerMove, true);
        document.addEventListener('pointerover', this.onPointerOver, true);
        document.addEventListener('pointerout', this.onPointerOut, true);
        document.addEventListener('pointerdown', this.onPointerDown, true);
        document.addEventListener('pointerup', this.onPointerUp, true);
        document.addEventListener('pointercancel', this.onPointerCancel, true);
        document.addEventListener('mouseleave', this.onPointerLeave, true);
        document.addEventListener('click', this.onClick, true);
        document.addEventListener('beforeinput', this.onBeforeInput, true);
        document.addEventListener('input', this.onInput, true);
        document.addEventListener('change', this.onChange, true);
        document.addEventListener('keydown', this.onKeyDown, true);
        document.addEventListener('focusin', this.onFocusIn, true);
        document.addEventListener('focusout', this.onFocusOut, true);
        document.addEventListener('toggle', this.onToggle, true);
        document.addEventListener('scroll', this.onDocumentScroll, true);
        window.addEventListener('scroll', this.onWindowScroll, { passive: true });
        window.addEventListener('resize', this.onCursorViewportChange, { passive: true });
        window.addEventListener('orientationchange', this.onCursorViewportChange);
        window.visualViewport?.addEventListener('resize', this.onCursorViewportChange);
        window.visualViewport?.addEventListener('scroll', this.onCursorViewportChange);
        window.addEventListener('blur', this.onPointerLeave);

        // v19: DOM mutation replay отключён полностью.
        // Target-id назначается лениво только для визуального курсора/locks/scroll.
        this.observer = null;
    }

    syncRouteAndConnection() {
        const nextPageKey = resolvePageKey();
        const changed = nextPageKey !== this.pageKey;

        if (changed) {
            if (this.joinedPageKey) this.leaveCurrentPage();
            this.pageKey = nextPageKey;
            this.users = [];
            this.lastUiUserCount = 0;
            this.lastPresenceMetaSignature = '';
            this.authoritativeEvents.clear();
            this.authoritativeRevisions.clear();
            this.authoritativePendingKeys.clear();
            this.lastRemoteApplyMs = 0;
            this.remoteApplySamples = [];
            this.clearRemoteVisuals();
            this.annotateTree(document.body);
            this.reportStatus();
        }

        if (!this.pageKey || !this.isEnabled()) {
            if (this.joinedPageKey) this.leaveCurrentPage();
            this.reportStatus({ connected: false });
            return;
        }

        if (!this.socket?.connected) {
            this.socket?.connect?.();
            this.reportStatus({ connected: false });
            return;
        }

        if (this.joinedPageKey !== this.pageKey && !this.joining) {
            this.joinCurrentPage();
            return;
        }

        if (this.joinedPageKey === this.pageKey) {
            this.syncPresenceMetadata();
        }
    }

    joinCurrentPage() {
        if (!this.pageKey || !this.socket?.connected || this.joining) return;
        this.joining = true;

        const presenceMetadata = this.getPresenceMetadata();

        this.socket.emit('adminRealtime:join', {
            token: window.localStorage.getItem('token'),
            pageKey: this.pageKey,
            ...presenceMetadata,
        }, (response = {}) => {
            this.joining = false;
            if (response?.ok && response.pageKey === this.pageKey) {
                this.joinedPageKey = this.pageKey;
                this.lastPresenceMetaSignature = JSON.stringify(presenceMetadata);
                this.users = Array.isArray(response.users) ? response.users : [];
                this.setBlockLocks(response.locks || []);
                this.annotateTree(document.body);
                this.applyAuthoritativeSnapshot(response.snapshot || null);
                this.reportStatus();
                this.scheduleAdmin3DStateSync(true, 60);
            } else {
                this.joinedPageKey = '';
                this.reportStatus({ connected: false });
            }
        });
    }

    leaveCurrentPage() {
        if (!this.joinedPageKey) return;
        const leaving = this.joinedPageKey;
        this.joinedPageKey = '';
        this.socket?.emit('adminRealtime:leave', { pageKey: leaving });
        this.ownedBlockIds.clear();
        this.pendingBlockIds.clear();
        this.blockLocks.clear();
        this.lastUiUserCount = 0;
        this.lastPresenceMetaSignature = '';
        this.authoritativeEvents.clear();
        this.authoritativeRevisions.clear();
        this.authoritativePendingKeys.clear();
        this.lastRemoteApplyMs = 0;
        this.remoteApplySamples = [];
        this.renderBlockLocks();
    }

    onConnect() {
        this.joinedPageKey = '';
        this.joining = false;
        this.lastPresenceMetaSignature = '';
        this.syncRouteAndConnection();
    }

    onDisconnect() {
        this.joinedPageKey = '';
        this.joining = false;
        this.users = [];
        this.lastUiUserCount = 0;
        this.lastPresenceMetaSignature = '';
        this.ownedBlockIds.clear();
        this.pendingBlockIds.clear();
        this.blockLocks.clear();
        this.authoritativeEvents.clear();
        this.authoritativeRevisions.clear();
        this.authoritativePendingKeys.clear();
        this.renderBlockLocks();
        this.clearRemoteVisuals();
        this.reportStatus({ connected: false, users: [] });
    }

    onUiUsers(payload = {}) {
        if (payload.pageKey !== this.pageKey) return;
        const previousCount = this.lastUiUserCount;
        this.users = Array.isArray(payload.users) ? payload.users : [];
        this.lastUiUserCount = this.users.length;
        this.reportStatus();

        // Когда к странице присоединяется второй экран, текущий участник
        // сразу публикует фактическое состояние открытого 3D-редактора.
        if (this.users.length !== previousCount) {
            this.scheduleAdmin3DStateSync(true, 70);
        }
    }

    onUiLocks(payload = {}) {
        if (payload.pageKey !== this.pageKey) return;
        this.setBlockLocks(payload.locks || []);
    }

    setBlockLocks(locks = []) {
        const next = new Map();
        (Array.isArray(locks) ? locks : []).forEach((lock) => {
            const blockId = String(lock?.blockId || '');
            if (!blockId) return;
            next.set(blockId, lock);
        });
        this.blockLocks = next;

        const ownSocketId = String(this.socket?.id || '');
        this.ownedBlockIds = new Set(
            Array.from(next.values())
                .filter((lock) => ownSocketId && String(lock.socketId || '') === ownSocketId)
                .map((lock) => String(lock.blockId || ''))
        );
        this.pendingBlockIds.forEach((blockId) => {
            if (this.ownedBlockIds.has(blockId)) this.pendingBlockIds.delete(blockId);
        });
        this.renderBlockLocks();
        this.reportStatus();
    }

    getCurrentUser() {
        const socketId = String(this.socket?.id || '');
        const user = this.users.find((item) => (
            String(item?.socketId || '') === socketId
            || (Array.isArray(item?.socketIds) && item.socketIds.map(String).includes(socketId))
        ));
        return user || { name: 'Вы', role: null, userId: '' };
    }

    getForeignLockForElement(element) {
        const field = nearestEditable(element, this.pageKey);
        if (!(field instanceof Element)) return null;

        const ownSocketId = String(this.socket?.id || '');
        const fieldId = ensureTargetId(field, this.pageKey);

        for (const [lockId, lock] of this.blockLocks.entries()) {
            if (!lock || String(lock.socketId || '') === ownSocketId) continue;

            if (fieldId && String(lockId) === String(fieldId)) {
                return lock;
            }

            const lockedField = this.lockElements.get(lockId);
            if (lockedField instanceof Element && lockedField === field) {
                return lock;
            }
        }

        return null;
    }

    showBlockedNotice(lock) {
        if (!lock) return;
        const fieldId = String(lock.blockId || 'unknown');
        const previous = Number(this.lastBlockNoticeAt.get(fieldId) || 0);
        if (now() - previous < BLOCK_LOCK_NOTICE_COOLDOWN_MS) return;
        this.lastBlockNoticeAt.set(fieldId, now());
        const name = lock.user?.name || 'другой участник';
        this.showActivity({
            socketId: lock.socketId,
            user: lock.user || { name },
        }, `Поле сейчас редактирует ${name}`);
    }

    guardForeignLock(event) {
        if (!event?.isTrusted || this.applyingRemote) return false;
        const target = event.target instanceof Element ? event.target : null;
        if (!target || !isInsidePage(target, this.pageKey)) return false;

        const field = nearestEditable(target, this.pageKey);
        if (!field) return false;

        const lock = this.getForeignLockForElement(field);
        if (!lock) return false;

        event.preventDefault?.();
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();

        if (document.activeElement === field) {
            field.blur?.();
        }

        this.showBlockedNotice(lock);
        return true;
    }

    requestBlockLock(element) {
        if (!this.canEmit()) return;

        const field = nearestEditable(element, this.pageKey) || element;
        if (!(field instanceof Element) || !field.matches(EDITABLE_SELECTOR)) return;

        const fieldId = ensureTargetId(field, this.pageKey);
        if (!fieldId) return;

        const releaseTimer = this.lockReleaseTimers.get(fieldId);
        if (releaseTimer) {
            window.clearTimeout(releaseTimer);
            this.lockReleaseTimers.delete(fieldId);
        }

        const foreign = this.blockLocks.get(fieldId);
        if (foreign && String(foreign.socketId || '') !== String(this.socket?.id || '')) {
            this.showBlockedNotice(foreign);
            field.blur?.();
            return;
        }

        if (this.ownedBlockIds.has(fieldId) || this.pendingBlockIds.has(fieldId)) return;

        this.pendingBlockIds.add(fieldId);

        const user = this.getCurrentUser();
        const optimistic = {
            pageKey: this.pageKey,
            blockId: fieldId,
            descriptor: buildDescriptor(field, this.pageKey),
            label: getFieldLabel(field, this.pageKey),
            socketId: this.socket?.id || '',
            user: {
                id: user.userId || user.id || '',
                name: user.name || 'Вы',
                role: user.role || null,
            },
            acquiredAt: now(),
            updatedAt: now(),
            expiresAt: now() + 15000,
        };

        this.blockLocks.set(fieldId, optimistic);
        this.renderBlockLocks();

        this.socket.emit('adminRealtime:blockLock', {
            token: window.localStorage.getItem('token'),
            pageKey: this.pageKey,
            blockId: fieldId,
            descriptor: optimistic.descriptor,
            label: optimistic.label,
        }, (response = {}) => {
            this.pendingBlockIds.delete(fieldId);

            if (response?.ok && response.lock) {
                this.blockLocks.set(fieldId, response.lock);
                this.ownedBlockIds.add(fieldId);
                this.renderBlockLocks();
                this.reportStatus();
                return;
            }

            this.ownedBlockIds.delete(fieldId);
            this.blockLocks.delete(fieldId);

            if (response?.lock) {
                this.blockLocks.set(fieldId, response.lock);
            }

            this.renderBlockLocks();

            if (response?.lock) {
                this.showBlockedNotice(response.lock);
            }

            if (document.activeElement === field) {
                field.blur?.();
            }
        });
    }

    scheduleBlockUnlock(element) {
        const field = nearestEditable(element, this.pageKey) || element;
        if (!(field instanceof Element) || !field.matches(EDITABLE_SELECTOR)) return;

        const fieldId = ensureTargetId(field, this.pageKey);
        if (!fieldId || !this.ownedBlockIds.has(fieldId)) return;

        const previous = this.lockReleaseTimers.get(fieldId);
        if (previous) window.clearTimeout(previous);

        const timer = window.setTimeout(() => {
            this.lockReleaseTimers.delete(fieldId);

            if (document.activeElement === field) return;

            this.releaseBlock(fieldId);
        }, BLOCK_LOCK_RELEASE_DELAY_MS);

        this.lockReleaseTimers.set(fieldId, timer);
    }

    releaseBlock(blockId) {
        const id = String(blockId || '');
        if (!id || !this.ownedBlockIds.has(id)) return;

        this.ownedBlockIds.delete(id);
        this.pendingBlockIds.delete(id);
        this.blockLocks.delete(id);
        this.renderBlockLocks();

        if (this.socket?.connected && this.joinedPageKey === this.pageKey) {
            this.socket.emit('adminRealtime:blockUnlock', {
                token: window.localStorage.getItem('token'),
                pageKey: this.pageKey,
                blockId: id,
            });
        }

        this.reportStatus();
    }

    heartbeatOwnedBlocks() {
        if (document.visibilityState === 'hidden' || !document.hasFocus()) return;
        if (!this.canEmit() || !this.ownedBlockIds.size) return;

        this.socket.emit('adminRealtime:blockHeartbeat', {
            token: window.localStorage.getItem('token'),
            pageKey: this.pageKey,
            blockIds: Array.from(this.ownedBlockIds),
        });
    }

    ensureLockBadgeLayer() {
        if (this.lockBadgeLayer?.isConnected) return this.lockBadgeLayer;

        const layer = document.createElement('div');
        layer.className = 'adminRealtimeUiFieldLockLayer';
        document.body.appendChild(layer);
        this.lockBadgeLayer = layer;
        return layer;
    }

    renderBlockLocks() {
        this.lockElements.clear();

        this.lockFrames.forEach((frame) => frame.remove());
        this.lockFrames.clear();

        this.lockBadges.forEach((badge) => badge.remove());
        this.lockBadges.clear();

        if (!this.pageKey || !this.blockLocks.size) {
            this.lockBadgeLayer?.remove();
            this.lockBadgeLayer = null;
            return;
        }

        const layer = this.ensureLockBadgeLayer();
        const ownSocketId = String(this.socket?.id || '');

        for (const [fieldId, lock] of this.blockLocks.entries()) {
            const resolved = findTargetDetailed({
                targetId: fieldId,
                descriptor: lock.descriptor,
            }, this.pageKey);

            const field = resolved?.node || null;
            if (!(field instanceof Element) || !field.matches(EDITABLE_SELECTOR)) continue;

            const visual = getFieldVisualElement(field) || field;
            const mine = String(lock.socketId || '') === ownSocketId;

            this.lockElements.set(fieldId, field);

            // Свой focus уже виден нативно. Рисуем ровно ОДИН realtime-слой
            // только для чужого lock — иначе собственная рамка дублирует UI.
            if (mine) continue;

            if (document.activeElement === field) {
                field.blur?.();
            }

            const color = colorForSocket(lock.socketId || fieldId);
            const frame = document.createElement('div');
            frame.className = 'adminRealtimeUiFieldLockFrame';
            frame.style.setProperty('--admin-lock-color', color);
            layer.appendChild(frame);
            this.lockFrames.set(fieldId, frame);

            const badge = document.createElement('div');
            badge.className = 'adminRealtimeUiFieldLockBadge';
            badge.style.setProperty('--admin-lock-color', color);
            badge.textContent = `Редактирует ${lock.user?.name || 'участник'} · ${lock.label || 'поле'}`;
            layer.appendChild(badge);
            this.lockBadges.set(fieldId, badge);

            // visual хранится отдельно от lock target: для checkbox/switch рамка идёт вокруг всей строки.
            frame.__adminRealtimeVisual = visual;
            badge.__adminRealtimeVisual = visual;
        }

        this.scheduleLockReflow();
    }

    scheduleLockReflow() {
        if (this.lockReflowFrame) return;

        this.lockReflowFrame = window.requestAnimationFrame(() => {
            this.lockReflowFrame = null;

            for (const [fieldId, frame] of this.lockFrames.entries()) {
                const visual = frame.__adminRealtimeVisual
                    || getFieldVisualElement(this.lockElements.get(fieldId))
                    || this.lockElements.get(fieldId);

                const rect = visual?.getBoundingClientRect?.();
                const badge = this.lockBadges.get(fieldId);

                if (!rect || rect.width <= 0 || rect.height <= 0) {
                    frame.style.transform = 'translate3d(-1000px,-1000px,0)';
                    if (badge) badge.style.transform = 'translate3d(-1000px,-1000px,0)';
                    continue;
                }

                const pad = 3;
                const left = Math.round(rect.left - pad);
                const top = Math.round(rect.top - pad);
                const width = Math.max(8, Math.round(rect.width + pad * 2));
                const height = Math.max(8, Math.round(rect.height + pad * 2));

                frame.style.width = `${width}px`;
                frame.style.height = `${height}px`;
                frame.style.transform = `translate3d(${left}px,${top}px,0)`;

                if (!badge) continue;

                const badgeWidth = Math.min(
                    340,
                    Math.max(150, Math.min(window.innerWidth - 16, Math.max(rect.width, 180)))
                );

                const x = clamp(
                    rect.left,
                    8,
                    Math.max(8, window.innerWidth - badgeWidth - 8)
                );

                const belowY = rect.bottom + 6;
                const placeAbove = belowY + 32 > window.innerHeight && rect.top > 40;
                const y = placeAbove
                    ? Math.max(8, rect.top - 31)
                    : Math.min(window.innerHeight - 30, belowY);

                badge.style.maxWidth = `${badgeWidth}px`;
                badge.style.transform = `translate3d(${Math.round(x)}px,${Math.round(y)}px,0)`;
            }
        });
    }

    getAdmin3DStateFromDom() {
        if (!this.pageKey) return null;

        const root = Array.from(document.querySelectorAll(ADMIN_3D_ROOT_SELECTOR))
            .find((node) => isInsidePage(node, this.pageKey));

        if (!(root instanceof Element)) return null;

        const scope = normalizeText(
            root.getAttribute('data-catalog-sync-scope') || '',
            180
        );

        const navSelect = root.querySelector('.adminProduct3DPreview__nav select');
        const pagerText = normalizeText(
            root.querySelector('.adminProduct3DPreview__pager span')?.textContent || '',
            80
        );
        const pagerMatch = pagerText.match(/(\d+)\s*\/\s*(\d+)/);

        const activeKeys = Array.from(root.querySelectorAll([
            'button.is-active',
            '[role="button"].is-active',
            '[role="tab"].is-active',
            '[aria-selected="true"]',
            '[aria-pressed="true"]',
        ].join(',')))
            .map((element) => getAdmin3DControlSemanticKey(element))
            .filter(Boolean)
            .slice(0, 48);

        const confirmOpen = Boolean(
            root.querySelector(ADMIN_3D_CONFIRM_SELECTOR)
        );

        return {
            scope,
            navValue: navSelect instanceof HTMLSelectElement
                ? String(navSelect.value || '')
                : '',
            pagerIndex: pagerMatch ? Math.max(0, Number(pagerMatch[1]) || 0) : 0,
            pagerTotal: pagerMatch ? Math.max(0, Number(pagerMatch[2]) || 0) : 0,
            activeKeys,
            confirmOpen,
        };
    }

    scheduleAdmin3DStateSync(_force = false, _delay = 80) {
        // v19: 3D UI синхронизируется явными React/domain adapters.
    }

    syncAdmin3DStateFromDom(_force = false) {
        // DOM preview replay отключён.
    }

    annotateTree(root) {
        if (!this.pageKey || !(root instanceof Element)) return;

        const nodes = [];
        if (root.matches?.(INTERACTIVE_SELECTOR) && isInsidePage(root, this.pageKey)) {
            nodes.push(root);
        }

        try {
            root.querySelectorAll?.(INTERACTIVE_SELECTOR).forEach((node) => {
                if (isInsidePage(node, this.pageKey)) nodes.push(node);
            });
        } catch (_error) {
            // no-op
        }

        nodes.forEach((node) => ensureTargetId(node, this.pageKey));
    }

    emit(type, element = null, extra = {}) {
        if (!this.canEmit()) return;

        const targetId = element instanceof Element
            ? ensureTargetId(element, this.pageKey)
            : String(extra.targetId || 'system');

        const descriptor = element instanceof Element
            ? buildDescriptor(element, this.pageKey)
            : null;

        this.sequence += 1;
        const payload = {
            token: window.localStorage.getItem('token'),
            pageKey: this.pageKey,
            type,
            targetId,
            descriptor,
            label: element ? getElementText(element) : normalizeText(extra.label || '', 180),
            sequence: this.sequence,
            ...extra,
        };

        /*
         * Cursor обязан быть volatile: если Wi-Fi на планшете на мгновение
         * просел, старые координаты нельзя ставить в очередь и проигрывать
         * потом. Старый sales-session именно так и отправлял курсор.
         */
        if (
            type === 'cursor'
            && this.socket?.volatile
            && typeof this.socket.volatile.emit === 'function'
        ) {
            this.socket.volatile.emit('adminRealtime:state', payload);
            return;
        }

        this.socket.emit('adminRealtime:state', payload);
    }

    onPointerMove(event) {
        if (!this.canEmit()) return;
        const sourceElement = event.target instanceof Element ? event.target : null;
        // Курсор показываем по всему окну активного ADMIN-раздела, включая
        // общую шапку. Служебную realtime-панель исключаем, чтобы она не
        // создавала сама на себя сетевые события.
        if (sourceElement && isExcluded(sourceElement)) return;

        const timestamp = now();
        if (timestamp - this.lastCursorAt < CURSOR_INTERVAL_MS) return;
        this.lastCursorAt = timestamp;

        const semantic = resolveCursorAnchorPayload({
            clientX: event.clientX,
            clientY: event.clientY,
            sourceElement,
            pageKey: this.pageKey,
        });

        /*
         * semantic.primary построен из DOM-элемента на ЭТОМ же клиенте.
         * Раньше мы после этого зачем-то снова искали его через findTarget(),
         * который ранжирует querySelectorAll по странице. Это происходило
         * ~33 раза/сек. Берём уже известный element напрямую.
         */
        const primaryElement = semantic.primaryElement instanceof Element
            ? semantic.primaryElement
            : null;

        this.emit('cursor', primaryElement, {
            targetId: semantic.primary?.targetId || 'cursor',
            descriptor: semantic.primary?.descriptor || null,
            x: clamp(event.clientX / Math.max(1, window.innerWidth), 0, 1),
            y: clamp(event.clientY / Math.max(1, window.innerHeight), 0, 1),
            // V4: полный стек якорей. Старые поля оставляем для совместимости
            // при поочерёдном обновлении frontend/backend.
            anchors: semantic.anchors,
            targetX: semantic.primary?.x ?? null,
            targetY: semantic.primary?.y ?? null,
            scopeTargetId: semantic.scope?.targetId || '',
            scopeDescriptor: semantic.scope?.descriptor || null,
            scopeX: semantic.scope?.x ?? null,
            scopeY: semantic.scope?.y ?? null,
            visible: true,
            pointerType: normalizeText(event.pointerType || 'mouse', 20),
        });
    }

    onPointerLeave() {
        if (!this.canEmit()) return;

        /*
         * Native <select> открывает системный picker вне DOM и браузер может
         * прислать mouseleave/window blur. Это не означает, что пользователь
         * ушёл со страницы. Пока picker открыт, держим remote cursor на select.
         */
        if (
            this.localOpenSelect instanceof Element
            && this.localOpenSelect.isConnected
        ) {
            this.emitCursorPinnedToSelect(this.localOpenSelect);
            return;
        }

        this.emit('cursor', null, {
            targetId: 'cursor',
            x: 0,
            y: 0,
            visible: false,
            pointerType: 'mouse',
        });

        if (this.lastHoverId) {
            this.emit('hover', null, {
                targetId: this.lastHoverId,
                visible: false,
            });
            this.lastHoverId = '';
        }
    }

    onPointerOver(event) {
        if (!this.canEmit()) return;
        const element = nearestInteractive(event.target, this.pageKey);
        if (!element) return;

        const id = ensureTargetId(element, this.pageKey);
        if (!id || id === this.lastHoverId) return;

        this.lastHoverId = id;
        this.emit('hover', element, { visible: true });
    }

    onPointerOut(event) {
        if (!this.canEmit()) return;
        const element = nearestInteractive(event.target, this.pageKey);
        if (!element) return;

        const related = nearestInteractive(event.relatedTarget, this.pageKey);
        if (related === element) return;

        const id = ensureTargetId(element, this.pageKey);
        if (!id || id !== this.lastHoverId) return;

        this.lastHoverId = '';
        this.emit('hover', element, { visible: false });
    }

    getNativeSelectElement(target) {
        const element = target instanceof Element ? target : null;
        if (!element) return null;
        const select = element.closest?.('select');
        if (!(select instanceof Element) || select.tagName !== 'SELECT') return null;
        if (!isInsidePage(select, this.pageKey) || isExcluded(select)) return null;
        return select;
    }

    emitNativeSelectVisual(select, open) {
        if (!(select instanceof Element) || select.tagName !== 'SELECT' || !this.canEmit()) return;

        const options = Array.from(select.options || []);
        const selectedAbsoluteIndex = Math.max(0, Number(select.selectedIndex) || 0);
        const maxOptions = Math.max(1, SELECT_VISUAL_MAX_OPTIONS);
        const maxStart = Math.max(0, options.length - maxOptions);
        const startIndex = Math.min(
            maxStart,
            Math.max(0, selectedAbsoluteIndex - Math.floor(maxOptions / 2))
        );
        const visibleOptions = options
            .slice(startIndex, startIndex + maxOptions)
            .map((option) => normalizeText(option?.textContent || option?.label || option?.value || '', 240));

        this.emit('select', select, {
            open: open === true,
            fieldLabel: getFieldLabel(select, this.pageKey),
            selectedText: normalizeText(
                options[selectedAbsoluteIndex]?.textContent
                || options[selectedAbsoluteIndex]?.label
                || options[selectedAbsoluteIndex]?.value
                || '',
                240
            ),
            options: visibleOptions,
            startIndex,
            selectedIndex: selectedAbsoluteIndex,
            totalOptions: options.length,
        });
    }

    rememberLocalSelectCursorAnchor(select, event = null) {
        if (!(select instanceof Element) || select.tagName !== 'SELECT') return;

        const rect = select.getBoundingClientRect();
        if (!(rect.width > 0) || !(rect.height > 0)) return;

        const rawClientX = Number(event?.clientX);
        const rawClientY = Number(event?.clientY);
        const hasPointerPoint = Number.isFinite(rawClientX) && Number.isFinite(rawClientY);

        const targetX = hasPointerPoint
            ? clamp((rawClientX - rect.left) / Math.max(1, rect.width), 0, 1)
            : 0.92;
        const targetY = hasPointerPoint
            ? clamp((rawClientY - rect.top) / Math.max(1, rect.height), 0, 1)
            : 0.5;

        this.localSelectCursorAnchor = {
            select,
            targetX,
            targetY,
        };
    }

    emitCursorPinnedToSelect(select) {
        if (!(select instanceof Element) || select.tagName !== 'SELECT' || !this.canEmit()) return;

        const rect = select.getBoundingClientRect();
        if (!(rect.width > 0) || !(rect.height > 0)) return;

        const remembered = (
            this.localSelectCursorAnchor?.select === select
            && this.localSelectCursorAnchor
        ) || null;

        const targetX = clamp(remembered?.targetX, 0, 1, 0.92);
        const targetY = clamp(remembered?.targetY, 0, 1, 0.5);
        const clientX = rect.left + rect.width * targetX;
        const clientY = rect.top + rect.height * targetY;

        const semantic = resolveCursorAnchorPayload({
            clientX,
            clientY,
            sourceElement: select,
            pageKey: this.pageKey,
        });

        this.emit('cursor', select, {
            targetId: semantic.primary?.targetId || ensureTargetId(select, this.pageKey) || 'cursor',
            descriptor: semantic.primary?.descriptor || buildDescriptor(select, this.pageKey),
            x: clamp(clientX / Math.max(1, window.innerWidth), 0, 1),
            y: clamp(clientY / Math.max(1, window.innerHeight), 0, 1),
            anchors: semantic.anchors,
            targetX: semantic.primary?.x ?? targetX,
            targetY: semantic.primary?.y ?? targetY,
            scopeTargetId: semantic.scope?.targetId || '',
            scopeDescriptor: semantic.scope?.descriptor || null,
            scopeX: semantic.scope?.x ?? null,
            scopeY: semantic.scope?.y ?? null,
            visible: true,
            pointerType: 'mouse',
        });
    }

    startLocalSelectCursorHeartbeat(select) {
        if (this.localSelectCursorHeartbeat) {
            window.clearInterval(this.localSelectCursorHeartbeat);
            this.localSelectCursorHeartbeat = null;
        }

        this.emitCursorPinnedToSelect(select);
        this.localSelectCursorHeartbeat = window.setInterval(() => {
            if (
                !(this.localOpenSelect instanceof Element)
                || this.localOpenSelect !== select
                || !select.isConnected
            ) {
                if (this.localSelectCursorHeartbeat) {
                    window.clearInterval(this.localSelectCursorHeartbeat);
                    this.localSelectCursorHeartbeat = null;
                }
                return;
            }

            this.emitCursorPinnedToSelect(select);
        }, SELECT_CURSOR_HEARTBEAT_MS);
    }

    stopLocalSelectCursorHeartbeat() {
        if (!this.localSelectCursorHeartbeat) return;
        window.clearInterval(this.localSelectCursorHeartbeat);
        this.localSelectCursorHeartbeat = null;
    }

    closeLocalNativeSelect(select = this.localOpenSelect) {
        if (!(select instanceof Element) || select.tagName !== 'SELECT') {
            this.localOpenSelect = null;
            return;
        }

        this.emitNativeSelectVisual(select, false);
        if (this.localOpenSelect === select) this.localOpenSelect = null;
        if (this.localSelectCursorAnchor?.select === select) {
            this.localSelectCursorAnchor = null;
        }
        this.stopLocalSelectCursorHeartbeat();
    }

    ensureSelectMirrorLayer() {
        if (this.selectMirrorLayer?.isConnected) return this.selectMirrorLayer;

        const layer = document.createElement('div');
        layer.className = 'adminRealtimeUiSelectLayer';
        document.body.appendChild(layer);
        this.selectMirrorLayer = layer;
        return layer;
    }

    getRemoteSelectKey(payload = {}) {
        return `${String(payload.socketId || 'remote')}:${String(payload.targetId || '')}`;
    }

    positionRemoteSelectMirror(key, payload = {}) {
        const node = this.remoteSelectMirrors.get(key);
        if (!node?.isConnected) return false;

        const target = findTarget(payload, this.pageKey);
        if (!(target instanceof Element)) return false;

        const rect = target.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return false;

        const viewportWidth = Math.max(1, window.innerWidth);
        const viewportHeight = Math.max(1, window.innerHeight);
        const width = Math.min(
            Math.max(180, rect.width),
            Math.max(180, Math.min(420, viewportWidth - 16))
        );

        node.style.width = `${Math.round(width)}px`;
        const measuredHeight = Math.max(80, Math.min(320, node.offsetHeight || 220));
        let left = rect.left;
        let top = rect.bottom + 4;

        if (top + measuredHeight > viewportHeight - 8) {
            top = Math.max(8, rect.top - measuredHeight - 4);
        }
        left = Math.max(8, Math.min(left, viewportWidth - width - 8));

        node.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
        return true;
    }

    scheduleSelectMirrorReflow() {
        if (!this.remoteSelectPayloads.size) return;
        window.requestAnimationFrame(() => {
            this.remoteSelectPayloads.forEach((payload, key) => {
                this.positionRemoteSelectMirror(key, payload);
            });
        });
    }

    removeRemoteSelectMirror(key) {
        const timer = this.remoteSelectTimers.get(key);
        if (timer) window.clearTimeout(timer);
        this.remoteSelectTimers.delete(key);
        this.remoteSelectPayloads.delete(key);

        const node = this.remoteSelectMirrors.get(key);
        node?.remove();
        this.remoteSelectMirrors.delete(key);

        if (!this.remoteSelectMirrors.size) {
            this.selectMirrorLayer?.remove();
            this.selectMirrorLayer = null;
        }
    }

    renderRemoteSelect(payload = {}) {
        const key = this.getRemoteSelectKey(payload);
        if (!payload.open) {
            this.removeRemoteSelectMirror(key);
            return true;
        }

        const target = findTarget(payload, this.pageKey);
        if (!(target instanceof Element) || target.tagName !== 'SELECT') return false;

        const layer = this.ensureSelectMirrorLayer();
        let node = this.remoteSelectMirrors.get(key);
        if (!node?.isConnected) {
            node = document.createElement('div');
            node.className = 'adminRealtimeUiSelectMirror';
            layer.appendChild(node);
            this.remoteSelectMirrors.set(key, node);
        }

        node.style.setProperty('--admin-remote-color', colorForSocket(payload.socketId || 'remote'));
        node.replaceChildren();

        const head = document.createElement('div');
        head.className = 'adminRealtimeUiSelectMirror__head';
        const actor = document.createElement('span');
        const fieldLabel = normalizeText(payload.fieldLabel || '', 120);
        actor.textContent = fieldLabel
            ? `${payload.user?.name || 'Администратор'} · ${fieldLabel}`
            : (payload.user?.name || 'Администратор');
        const counter = document.createElement('span');
        const total = Math.max(0, Number(payload.totalOptions) || 0);
        counter.textContent = total > (payload.options?.length || 0)
            ? `${payload.options?.length || 0} из ${total}`
            : `${total || payload.options?.length || 0}`;
        head.append(actor, counter);
        node.appendChild(head);

        if (payload.selectedText) {
            const current = document.createElement('div');
            current.className = 'adminRealtimeUiSelectMirror__current';
            current.textContent = `Выбрано: ${payload.selectedText}`;
            node.appendChild(current);
        }

        const optionsNode = document.createElement('div');
        optionsNode.className = 'adminRealtimeUiSelectMirror__options';
        const startIndex = Math.max(0, Number(payload.startIndex) || 0);
        const selectedIndex = Math.max(0, Number(payload.selectedIndex) || 0);
        (Array.isArray(payload.options) ? payload.options : []).forEach((text, index) => {
            const option = document.createElement('div');
            option.className = 'adminRealtimeUiSelectMirror__option';
            if (startIndex + index === selectedIndex) option.classList.add('is-selected');
            option.textContent = text || '—';
            optionsNode.appendChild(option);
        });
        node.appendChild(optionsNode);

        this.remoteSelectPayloads.set(key, payload);
        this.positionRemoteSelectMirror(key, payload);

        const previousTimer = this.remoteSelectTimers.get(key);
        if (previousTimer) window.clearTimeout(previousTimer);
        this.remoteSelectTimers.set(key, window.setTimeout(
            () => this.removeRemoteSelectMirror(key),
            SELECT_VISUAL_AUTO_CLOSE_MS
        ));
        return true;
    }

    onPointerDown(event) {
        if (this.guardForeignLock(event)) return;
        if (!this.canEmit()) return;

        const nativeSelect = this.getNativeSelectElement(event.target);
        if (this.localOpenSelect && this.localOpenSelect !== nativeSelect) {
            this.closeLocalNativeSelect(this.localOpenSelect);
        }
        if (nativeSelect) {
            this.localOpenSelect = nativeSelect;
            this.rememberLocalSelectCursorAnchor(nativeSelect, event);
            this.emitNativeSelectVisual(nativeSelect, true);
            this.startLocalSelectCursorHeartbeat(nativeSelect);
        }

        const editable = nearestEditable(event.target, this.pageKey);
        if (editable) this.requestBlockLock(editable);

        const element = nearestInteractive(event.target, this.pageKey);
        if (!element) return;

        this.lastPressedId = ensureTargetId(element, this.pageKey);
        this.emit('press', element, { pressed: true, visible: true });
    }

    onPointerUp() {
        if (!this.canEmit() || !this.lastPressedId) return;
        this.emit('press', null, {
            targetId: this.lastPressedId,
            pressed: false,
            visible: false,
        });
        this.lastPressedId = '';
    }

    onClick(event) {
        // v19: remote action replay запрещён. Клик другого пользователя
        // никогда не запускает действие на этом устройстве. Оставляем только
        // защиту semantic field-lock.
        this.guardForeignLock(event);
    }

    onBeforeInput(event) {
        if (!event?.isTrusted || this.applyingRemote) return;
        const editable = nearestEditable(event.target, this.pageKey);
        if (!editable) return;
        const lock = this.getForeignLockForElement(editable);
        if (lock) {
            event.preventDefault?.();
            event.stopPropagation?.();
            event.stopImmediatePropagation?.();
            this.showBlockedNotice(lock);
            return;
        }

        const fieldId = ensureTargetId(editable, this.pageKey);
        if (fieldId && this.pendingBlockIds.has(fieldId) && !this.ownedBlockIds.has(fieldId)) {
            event.preventDefault?.();
        }
    }

    onInput(event) {
        // Значения синхронизируются только явными domain/semantic adapters.
        // Но открытый native select может менять selectedIndex через клавиши;
        // follower-visual обновляем без DOM value replay.
        const nativeSelect = this.getNativeSelectElement(event?.target);
        if (
            nativeSelect
            && this.localOpenSelect === nativeSelect
        ) {
            this.emitNativeSelectVisual(nativeSelect, true);
            this.emitCursorPinnedToSelect(nativeSelect);
        }
    }

    onChange(event) {
        // Generic DOM value replay отключён. Финальный selected option сначала
        // показываем follower-у, затем закрываем visual. Само значение формы
        // синхронизирует React semantic/domain adapter страницы.
        const nativeSelect = this.getNativeSelectElement(event?.target);
        if (!nativeSelect) return;

        this.emitNativeSelectVisual(nativeSelect, true);
        this.emitCursorPinnedToSelect(nativeSelect);

        window.setTimeout(() => {
            if (this.localOpenSelect === nativeSelect) {
                this.closeLocalNativeSelect(nativeSelect);
            }
        }, 140);
    }

    onKeyDown(event) {
        if (!event?.isTrusted || this.applyingRemote || !this.canEmit()) return;
        const nativeSelect = this.getNativeSelectElement(event.target);
        if (!nativeSelect) return;

        if (event.key === 'Escape') {
            this.closeLocalNativeSelect(nativeSelect);
            return;
        }

        if (['Enter', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) {
            this.localOpenSelect = nativeSelect;
            if (this.localSelectCursorAnchor?.select !== nativeSelect) {
                this.rememberLocalSelectCursorAnchor(nativeSelect);
            }
            this.emitNativeSelectVisual(nativeSelect, true);
            this.startLocalSelectCursorHeartbeat(nativeSelect);

            // keydown приходит до изменения selectedIndex у native select.
            window.setTimeout(() => {
                if (this.localOpenSelect !== nativeSelect) return;
                this.emitNativeSelectVisual(nativeSelect, true);
                this.emitCursorPinnedToSelect(nativeSelect);
            }, 0);
        }
    }

    onFocusIn(event) {
        if (this.guardForeignLock(event)) return;
        if (!this.canEmit()) return;
        const element = nearestInteractive(event.target, this.pageKey);
        if (!element) return;

        const editable = nearestEditable(event.target, this.pageKey);
        if (editable) this.requestBlockLock(editable);

        this.lastFocusId = ensureTargetId(element, this.pageKey);
        this.emit('focus', element, { visible: true });
    }

    onFocusOut(event) {
        const focusElement = event.target instanceof Element && isInsidePage(event.target, this.pageKey)
            ? event.target
            : null;

        const nativeSelect = this.getNativeSelectElement(event?.target);
        if (
            nativeSelect
            && this.localOpenSelect === nativeSelect
        ) {
            window.setTimeout(() => {
                if (
                    this.localOpenSelect === nativeSelect
                    && document.activeElement !== nativeSelect
                ) {
                    this.closeLocalNativeSelect(nativeSelect);
                }
            }, 0);
        }

        if (focusElement) this.scheduleBlockUnlock(focusElement);

        if (!this.canEmit()) return;
        const element = nearestInteractive(event.target, this.pageKey);
        if (!element) return;

        const id = ensureTargetId(element, this.pageKey);
        if (id === this.lastFocusId) this.lastFocusId = '';
        this.emit('focus', element, { visible: false });
    }

    onToggle(_event) {
        // details/open состояние передаётся только явным semantic state.
    }

    onDocumentScroll(event) {
        this.scheduleCursorReflow();
        this.scheduleLockReflow();
        this.scheduleSelectMirrorReflow();
        if (!this.config?.syncScroll || !this.canEmit()) return;
        const element = event.target instanceof Element ? event.target : null;
        if (!element || !isInsidePage(element, this.pageKey)) return;

        const maxTop = Math.max(0, element.scrollHeight - element.clientHeight);
        const maxLeft = Math.max(0, element.scrollWidth - element.clientWidth);
        if (maxTop <= 0 && maxLeft <= 0) return;

        const targetId = ensureTargetId(element, this.pageKey);
        const timestamp = now();
        const previous = Number(this.lastScrollAt.get(targetId) || 0);
        if (timestamp - previous < SCROLL_INTERVAL_MS) return;
        this.lastScrollAt.set(targetId, timestamp);

        this.emit('scroll', element, {
            topRatio: maxTop > 0 ? clamp(element.scrollTop / maxTop, 0, 1) : 0,
            leftRatio: maxLeft > 0 ? clamp(element.scrollLeft / maxLeft, 0, 1) : 0,
            windowScroll: false,
        });
    }

    onWindowScroll() {
        this.scheduleCursorReflow();
        this.scheduleLockReflow();
        this.scheduleSelectMirrorReflow();
        if (!this.config?.syncScroll || !this.canEmit()) return;
        const timestamp = now();
        const previous = Number(this.lastScrollAt.get('__window__') || 0);
        if (timestamp - previous < SCROLL_INTERVAL_MS) return;
        this.lastScrollAt.set('__window__', timestamp);

        const maxTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const maxLeft = Math.max(0, document.documentElement.scrollWidth - window.innerWidth);

        this.emit('scroll', null, {
            targetId: '__window__',
            topRatio: maxTop > 0 ? clamp(window.scrollY / maxTop, 0, 1) : 0,
            leftRatio: maxLeft > 0 ? clamp(window.scrollX / maxLeft, 0, 1) : 0,
            windowScroll: true,
        });
    }

    registerControls(controls) {
        if (!this.config?.syncCamera || !controls?.object || !controls?.domElement) return;
        const dom = controls.domElement instanceof Element ? controls.domElement : null;
        if (!dom || !isInsidePage(dom, this.pageKey)) return;

        const targetId = ensureTargetId(dom, this.pageKey);
        if (!targetId) return;

        this.controlsByTarget.set(targetId, controls);
        controls.__adminRealtimeTargetId = targetId;
        controls.__adminRealtimeDescriptor = buildDescriptor(dom, this.pageKey);

        // Камера могла прийти раньше, чем React Three Fiber создал OrbitControls.
        // После регистрации пробуем только pending authoritative keys, а не всю
        // историю событий.
        if (this.authoritativePendingKeys.size) {
            this.scheduleAuthoritativeReplay();
        }
    }

    handleOrbitEvent(controls, event = {}) {
        if (!this.config?.syncCamera || !this.canEmit() || this.remoteCameraApplying) return;
        if (!controls?.object || !controls?.target) return;
        if (!['change', 'end'].includes(event?.type)) return;

        this.registerControls(controls);
        const timestamp = now();
        const previous = Number(this.lastCameraAt.get(controls) || 0);
        if (event.type !== 'end' && timestamp - previous < CAMERA_INTERVAL_MS) return;
        this.lastCameraAt.set(controls, timestamp);

        const camera = controls.object;
        const targetId = controls.__adminRealtimeTargetId || 'camera';
        const dom = controls.domElement instanceof Element ? controls.domElement : null;

        this.emit('camera', dom, {
            targetId,
            viewportKey: targetId,
            position: camera.position.toArray().map((value) => round(value, 6)),
            target: controls.target.toArray().map((value) => round(value, 6)),
            zoom: round(camera.zoom || 1, 5),
            fov: round(camera.fov || 30, 5),
            final: event.type === 'end',
        });
    }

    resolveCursorAnchor(anchor = {}, socketId = '') {
        const signature = cursorAnchorSignature(anchor);
        const cacheKey = String(socketId || 'remote');
        const cached = this.cursorAnchorCache.get(cacheKey);

        if (
            cached
            && cached.signature === signature
            && cached.element instanceof Element
            && cached.element.isConnected
            && isInsidePage(cached.element, this.pageKey)
        ) {
            return {
                element: cached.element,
                score: cached.score,
                exact: cached.exact,
            };
        }

        const result = findTargetDetailed(anchor, this.pageKey);
        if (
            result.node instanceof Element
            && (result.exact || result.score >= CURSOR_MATCH_MIN_SCORE)
        ) {
            this.cursorAnchorCache.set(cacheKey, {
                signature,
                element: result.node,
                score: result.score,
                exact: result.exact,
            });
            return {
                element: result.node,
                score: result.score,
                exact: result.exact,
            };
        }

        return null;
    }

    resolveRemoteCursorPosition(payload = {}, socketId = '') {
        const anchors = Array.isArray(payload.anchors)
            ? payload.anchors.slice(0, CURSOR_ANCHOR_LIMIT)
            : [];

        // Если для текущего semantic stack уже выбран уверенный якорь, не
        // пересканируем огромный DOM на каждом mousemove.
        const cached = this.cursorAnchorCache.get(String(socketId || 'remote'));
        if (
            cached?.element instanceof Element
            && cached.element.isConnected
            && isInsidePage(cached.element, this.pageKey)
        ) {
            const cachedAnchor = anchors.find(
                (anchor) => cursorAnchorSignature(anchor) === cached.signature
            );
            if (cachedAnchor) {
                const rect = cached.element.getBoundingClientRect?.();
                if (rect?.width > 0 && rect?.height > 0) {
                    return {
                        x: rect.left + rect.width * clamp(cachedAnchor.x, 0, 1),
                        y: rect.top + rect.height * clamp(cachedAnchor.y, 0, 1),
                        anchor: cached.element,
                        anchorKind: cachedAnchor.kind || 'surface',
                        matchScore: cached.score,
                    };
                }
            }
        }

        /*
         * V4: первый уверенно найденный якорь выигрывает. Самое важное —
         * никогда не ставить курсор на "примерно похожий" control. Если control
         * неоднозначен, идём к row/card/scope/root.
         */
        for (const anchor of anchors) {
            const resolved = this.resolveCursorAnchor(anchor, socketId);
            if (!resolved?.element) continue;

            const rect = resolved.element.getBoundingClientRect?.();
            if (!rect || !(rect.width > 0) || !(rect.height > 0)) continue;

            return {
                x: rect.left + rect.width * clamp(anchor.x, 0, 1),
                y: rect.top + rect.height * clamp(anchor.y, 0, 1),
                anchor: resolved.element,
                anchorKind: anchor.kind || 'surface',
                matchScore: resolved.score,
            };
        }

        const resolveLegacy = (candidatePayload, ratioX, ratioY) => {
            const result = findTargetDetailed(candidatePayload, this.pageKey);
            const element = result.node;
            const rect = element?.getBoundingClientRect?.();

            if (
                element instanceof Element
                && (result.exact || result.score >= CURSOR_MATCH_MIN_SCORE)
                && rect
                && rect.width > 0
                && rect.height > 0
            ) {
                return {
                    x: rect.left + rect.width * clamp(ratioX, 0, 1),
                    y: rect.top + rect.height * clamp(ratioY, 0, 1),
                    anchor: element,
                    anchorKind: 'legacy',
                    matchScore: result.score,
                };
            }

            return null;
        };

        // Совместимость с v3-пакетом на втором клиенте во время обновления.
        if (
            payload.targetId
            && payload.targetId !== 'cursor'
            && payload.targetX !== null
            && payload.targetX !== undefined
            && payload.targetY !== null
            && payload.targetY !== undefined
        ) {
            const exact = resolveLegacy(payload, payload.targetX, payload.targetY);
            if (exact) return exact;
        }

        if (
            (payload.scopeTargetId || payload.scopeDescriptor)
            && payload.scopeX !== null
            && payload.scopeX !== undefined
            && payload.scopeY !== null
            && payload.scopeY !== undefined
        ) {
            const scoped = resolveLegacy({
                targetId: payload.scopeTargetId,
                descriptor: payload.scopeDescriptor,
            }, payload.scopeX, payload.scopeY);
            if (scoped) return scoped;
        }

        this.cursorAnchorCache.delete(String(socketId || 'remote'));

        // Самый последний fallback — только viewport. Он больше не может
        // перебить уверенно найденный DOM-якорь.
        return {
            x: clamp(payload.x, 0, 1) * Math.max(1, window.innerWidth),
            y: clamp(payload.y, 0, 1) * Math.max(1, window.innerHeight),
            anchor: null,
            anchorKind: 'viewport',
            matchScore: 0,
        };
    }

    ensureCursorAnimation() {
        if (this.cursorAnimationFrame || !this.cursorMotion.size) return;

        const animate = () => {
            this.cursorAnimationFrame = null;
            let needsNextFrame = false;

            this.cursorMotion.forEach((motion, socketId) => {
                const node = this.cursorNodes.get(String(socketId));
                if (!node || !motion) return;

                if (!Number.isFinite(motion.currentX) || !Number.isFinite(motion.currentY)) {
                    motion.currentX = motion.targetX;
                    motion.currentY = motion.targetY;
                }

                const dx = motion.targetX - motion.currentX;
                const dy = motion.targetY - motion.currentY;
                const distance = Math.hypot(dx, dy);

                if (distance <= CURSOR_SNAP_DISTANCE) {
                    motion.currentX = motion.targetX;
                    motion.currentY = motion.targetY;
                } else {
                    const factor = distance > 140
                        ? CURSOR_LERP_FAR
                        : CURSOR_LERP_NEAR;
                    motion.currentX += dx * factor;
                    motion.currentY += dy * factor;
                    needsNextFrame = true;
                }

                node.style.transform = `translate3d(${motion.currentX.toFixed(2)}px, ${motion.currentY.toFixed(2)}px, 0)`;
            });

            if (needsNextFrame && !this.destroyed) {
                this.cursorAnimationFrame = window.requestAnimationFrame(animate);
            }
        };

        this.cursorAnimationFrame = window.requestAnimationFrame(animate);
    }

    positionRemoteCursor(socketId, payload = {}) {
        const key = String(socketId);
        const node = this.cursorNodes.get(key);
        if (!node || payload.visible === false) return;

        const position = this.resolveRemoteCursorPosition(payload, key);
        const previous = this.cursorMotion.get(key);
        const motion = previous || {
            currentX: position.x,
            currentY: position.y,
            targetX: position.x,
            targetY: position.y,
        };

        /*
         * При смене semantic anchor не тянем курсор через пол-экрана от старой
         * кнопки к новой: для большого прыжка сразу подходим ближе к цели.
         */
        const jump = Math.hypot(
            position.x - Number(motion.currentX || 0),
            position.y - Number(motion.currentY || 0)
        );
        if (jump > 520) {
            motion.currentX = position.x;
            motion.currentY = position.y;
        }

        motion.targetX = position.x;
        motion.targetY = position.y;
        motion.anchor = position.anchor;
        motion.anchorKind = position.anchorKind;
        motion.matchScore = position.matchScore;
        this.cursorMotion.set(key, motion);
        this.ensureCursorAnimation();
    }

    scheduleCursorReflow() {
        if (this.cursorReflowFrame || !this.remoteCursorPayloads.size) return;

        this.cursorReflowFrame = window.requestAnimationFrame(() => {
            this.cursorReflowFrame = null;
            this.remoteCursorPayloads.forEach((payload, socketId) => {
                this.positionRemoteCursor(socketId, payload);
            });
        });
    }

    onCursorViewportChange() {
        this.scheduleCursorReflow();
        this.scheduleLockReflow();
        this.scheduleSelectMirrorReflow();
    }

    getRemoteCursorIdentity(payload = {}) {
        const socketId = String(payload.socketId || 'remote');
        const userId = String(
            payload.user?.id
            || payload.user?.userId
            || payload.userId
            || ''
        ).trim();

        return userId
            ? `user:${userId}`
            : `socket:${socketId}`;
    }

    clearRemoteCursorForSocket(socketId = '') {
        const socketKey = String(socketId || '');
        if (!socketKey) return;

        const identityKey = this.cursorIdentityBySocket.get(socketKey)
            || `socket:${socketKey}`;
        const ownerSocketId = String(
            this.cursorOwnerSocketByIdentity.get(identityKey) || ''
        );

        this.cursorIdentityBySocket.delete(socketKey);
        this.remoteCursorSequences.delete(socketKey);

        /*
         * Если у того же пользователя открыта другая вкладка и именно она
         * последней двигала курсор, уход старой вкладки не должен скрывать
         * актуальный курсор человека.
         */
        if (ownerSocketId && ownerSocketId !== socketKey) return;

        const node = this.cursorNodes.get(identityKey);
        node?.remove();
        this.cursorNodes.delete(identityKey);

        const timer = this.cursorTimers.get(identityKey);
        if (timer) window.clearTimeout(timer);
        this.cursorTimers.delete(identityKey);

        this.remoteCursorPayloads.delete(identityKey);
        this.cursorAnchorCache.delete(identityKey);
        this.cursorMotion.delete(identityKey);
        this.cursorOwnerSocketByIdentity.delete(identityKey);
    }

    renderRemoteCursor(payload) {
        const socketId = String(payload.socketId || 'remote');
        const identityKey = this.getRemoteCursorIdentity(payload);
        const incomingSequence = Math.max(0, Number(payload.sequence) || 0);
        const previousSequence = Number(this.remoteCursorSequences.get(socketId) || 0);

        if (incomingSequence && previousSequence && incomingSequence < previousSequence) return;
        if (incomingSequence) this.remoteCursorSequences.set(socketId, incomingSequence);

        const previousIdentity = this.cursorIdentityBySocket.get(socketId);
        if (previousIdentity && previousIdentity !== identityKey) {
            this.clearRemoteCursorForSocket(socketId);
        }
        this.cursorIdentityBySocket.set(socketId, identityKey);

        const currentOwner = String(
            this.cursorOwnerSocketByIdentity.get(identityKey) || ''
        );

        if (payload.visible === false) {
            /*
             * Hide от неактуальной вкладки того же пользователя не скрывает
             * курсор, который уже двигается из другой вкладки.
             */
            if (!currentOwner || currentOwner === socketId) {
                const node = this.cursorNodes.get(identityKey);
                node?.classList.remove('is-visible');

                this.remoteCursorPayloads.delete(identityKey);
                this.cursorAnchorCache.delete(identityKey);
                this.cursorMotion.delete(identityKey);

                const timer = this.cursorTimers.get(identityKey);
                if (timer) window.clearTimeout(timer);
                this.cursorTimers.delete(identityKey);
                this.cursorOwnerSocketByIdentity.delete(identityKey);
            }
            return;
        }

        this.cursorOwnerSocketByIdentity.set(identityKey, socketId);

        if (!this.cursorLayer) {
            this.cursorLayer = document.createElement('div');
            this.cursorLayer.className = 'adminRealtimeUiCursorLayer';
            document.body.appendChild(this.cursorLayer);
        }

        let node = this.cursorNodes.get(identityKey);
        if (!node) {
            node = document.createElement('div');
            node.className = 'adminRealtimeUiCursor';
            node.style.setProperty(
                '--admin-remote-color',
                colorForSocket(identityKey)
            );
            node.innerHTML = `
                <svg class="adminRealtimeUiCursor__arrow" viewBox="0 0 24 30" aria-hidden="true">
                    <path d="M0 0V23L6.4 17.1L11.2 28.2L15.5 26.3L10.8 15.7H20.1L0 0Z"></path>
                </svg>
                <span class="adminRealtimeUiCursor__label"></span>
            `;
            this.cursorLayer.appendChild(node);
            this.cursorNodes.set(identityKey, node);
        }

        const label = node.querySelector('.adminRealtimeUiCursor__label');
        if (label) {
            label.textContent = payload.user?.name
                || payload.name
                || 'Администратор';
        }

        this.remoteCursorPayloads.set(identityKey, payload);
        this.positionRemoteCursor(identityKey, payload);
        node.classList.add('is-visible');

        const previousTimer = this.cursorTimers.get(identityKey);
        if (previousTimer) window.clearTimeout(previousTimer);

        const ownerAtSchedule = socketId;
        const timer = window.setTimeout(() => {
            if (
                String(this.cursorOwnerSocketByIdentity.get(identityKey) || '')
                !== ownerAtSchedule
            ) {
                return;
            }

            node?.classList.remove('is-visible');
            this.remoteCursorPayloads.delete(identityKey);
            this.cursorAnchorCache.delete(identityKey);
            this.cursorMotion.delete(identityKey);
            this.cursorTimers.delete(identityKey);
            this.cursorOwnerSocketByIdentity.delete(identityKey);
        }, 2600);

        this.cursorTimers.set(identityKey, timer);
    }

    flashTarget(element, attr, active, payload) {
        if (!(element instanceof Element)) return;
        const color = colorForSocket(payload.socketId || 'remote');
        element.style.setProperty('--admin-remote-color', color);
        element.setAttribute(attr, active ? 'true' : 'false');

        const key = `${payload.socketId || 'remote'}:${attr}:${payload.targetId || ''}`;
        if (!active) return;

        window.setTimeout(() => {
            if (element.isConnected) element.setAttribute(attr, 'false');
        }, REMOTE_VISUAL_MS);
    }

    showActivity(payload, text) {
        if (!this.activityLayer) {
            this.activityLayer = document.createElement('div');
            this.activityLayer.className = 'adminRealtimeUiActivityLayer';
            document.body.appendChild(this.activityLayer);
        }

        const node = document.createElement('div');
        node.className = 'adminRealtimeUiActivity';
        node.style.setProperty('--admin-remote-color', colorForSocket(payload.socketId || 'remote'));
        node.textContent = text || payload.label || 'Действие';

        const detail = document.createElement('span');
        detail.textContent = payload.user?.name || 'Администратор';
        node.appendChild(detail);
        this.activityLayer.appendChild(node);

        window.setTimeout(() => node.remove(), 1900);
    }

    getAuthoritativeEventKey(payload = {}) {
        if (payload.snapshotKey) return String(payload.snapshotKey);

        const targetId = String(payload.targetId || '');

        if (payload.type === 'preview') {
            const scope = String(payload.scope || targetId || '');
            return scope ? `preview:${scope}` : '';
        }

        if (payload.type === 'camera') {
            const viewportKey = String(payload.viewportKey || targetId || '');
            return viewportKey ? `camera:${viewportKey}` : '';
        }

        if (payload.type === 'scroll') {
            const scrollKey = targetId || (payload.windowScroll === true ? '__window__' : '');
            return scrollKey ? `scroll:${scrollKey}` : '';
        }

        if (payload.type === 'state') {
            const namespace = String(payload.state?.namespace || '');
            const stateKey = String(
                payload.state?.stateKey
                || payload.state?.action
                || ''
            );
            return namespace && stateKey
                ? `semantic:${namespace}:${stateKey}`
                : '';
        }

        return '';
    }

    rememberAuthoritativeEvent(payload = {}) {
        const key = this.getAuthoritativeEventKey(payload);
        if (!key) return true;

        const incomingRevision = Math.max(0, Number(payload.stateRevision || 0));
        const previousRevision = Math.max(
            0,
            Number(this.authoritativeRevisions.get(key) || 0)
        );

        if (
            incomingRevision > 0
            && previousRevision > 0
            && incomingRevision <= previousRevision
        ) {
            return false;
        }

        this.authoritativeEvents.set(key, payload);
        if (incomingRevision > 0) {
            this.authoritativeRevisions.set(key, incomingRevision);
        }

        return true;
    }

    pruneAuthoritativePending() {
        const timestamp = now();
        for (const [key, expiresAt] of this.authoritativePendingKeys.entries()) {
            if (Number(expiresAt || 0) <= timestamp) {
                this.authoritativePendingKeys.delete(key);
            }
        }
    }

    applyRememberedAuthoritative(payload = {}) {
        const key = this.getAuthoritativeEventKey(payload);
        if (!key) return this.applyRemoteEvent(payload, { allowQueue: false });

        if (
            payload.type === 'state'
            && COMPONENT_STATE_NAMESPACES.has(String(payload.state?.namespace || ''))
        ) {
            // React-компонент получит semantic state отдельным listener-ом.
            this.authoritativePendingKeys.delete(key);
            return true;
        }

        const applied = this.applyRemoteEvent(payload, { allowQueue: false });
        if (applied) this.authoritativePendingKeys.delete(key);
        else this.authoritativePendingKeys.set(key, now() + AUTHORITATIVE_PENDING_TTL_MS);
        return applied;
    }

    applyAuthoritativeSnapshot(snapshot = null) {
        if (!snapshot || snapshot.pageKey !== this.pageKey) return;

        const states = Array.isArray(snapshot.states) ? snapshot.states : [];
        states.forEach((payload) => {
            if (!payload || payload.pageKey !== this.pageKey) return;
            if (!this.rememberAuthoritativeEvent(payload)) return;
            this.applyRememberedAuthoritative(payload);
        });
    }

    onUiSnapshot(snapshot = {}) {
        if (!snapshot || snapshot.pageKey !== this.pageKey) return;
        this.applyAuthoritativeSnapshot(snapshot);
    }

    scheduleAuthoritativeReplay() {
        if (this.authoritativeReplayFrame || !this.authoritativePendingKeys.size) return;

        this.authoritativeReplayFrame = window.requestAnimationFrame(() => {
            this.authoritativeReplayFrame = null;
            this.pruneAuthoritativePending();

            // Snapshot Map уже содержит только последнее состояние каждого key.
            // Повторяем ТОЛЬКО те ключи, target которых реально отсутствовал.
            const keys = Array.from(this.authoritativePendingKeys.keys());
            keys.forEach((key) => {
                const payload = this.authoritativeEvents.get(key);
                if (!payload) {
                    this.authoritativePendingKeys.delete(key);
                    return;
                }
                this.applyRememberedAuthoritative(payload);
            });
        });
    }

    applyRemoteEvent(payload, { allowQueue = true } = {}) {
        if (payload.type === 'cursor') {
            this.renderRemoteCursor(payload);
            return true;
        }

        if (payload.type === 'clear') {
            this.clearRemoteVisuals(payload.socketId);
            return true;
        }

        if (payload.type === 'preview') return true;

        if (payload.type === 'select') {
            return this.renderRemoteSelect(payload);
        }

        /*
         * Компонентные semantic-state события обрабатываются самим React-
         * компонентом (PricingPolicyPanel). Generic bridge не должен искать
         * для них DOM-target и тем более складывать их в pending replay.
         * В v8 именно preview-progress каждые ~180 мс попадал ещё и сюда,
         * не находил target и создавал лишнюю очередь/DOM-поиск.
         */
        if (payload.type === 'state') return true;

        if (payload.type === 'scroll' && payload.windowScroll) {
            const maxTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
            const maxLeft = Math.max(0, document.documentElement.scrollWidth - window.innerWidth);

            this.applyingRemote = true;
            this.suppressUntil = now() + REMOTE_SUPPRESS_MS;
            try {
                window.scrollTo({
                    top: maxTop * clamp(payload.topRatio, 0, 1),
                    left: maxLeft * clamp(payload.leftRatio, 0, 1),
                    behavior: 'auto',
                });
            } finally {
                this.applyingRemote = false;
            }
            return true;
        }

        const target = findTarget(payload, this.pageKey);
        if (!target) {
            // Stateful события уже сохранены в authoritativeEvents и будут
            // применены после появления target. Transient/imperative события
            // намеренно не проигрываются через секунды: поздний click/hover
            // опаснее пропущенного и способен вернуть UI в старое состояние.
            return false;
        }

        ensureTargetId(target, this.pageKey);

        if (payload.type === 'hover') {
            this.flashTarget(target, 'data-admin-remote-hover', payload.visible !== false, payload);
            return true;
        }

        if (payload.type === 'press') {
            this.flashTarget(target, 'data-admin-remote-pressed', payload.pressed === true, payload);
            return true;
        }

        if (payload.type === 'focus') {
            this.flashTarget(target, 'data-admin-remote-focus', payload.visible !== false, payload);
            return true;
        }


        this.applyingRemote = true;
        this.suppressUntil = now() + REMOTE_SUPPRESS_MS;
        try {

            if (payload.type === 'scroll') {
                const maxTop = Math.max(0, target.scrollHeight - target.clientHeight);
                const maxLeft = Math.max(0, target.scrollWidth - target.clientWidth);
                target.scrollTop = maxTop * clamp(payload.topRatio, 0, 1);
                target.scrollLeft = maxLeft * clamp(payload.leftRatio, 0, 1);
                return true;
            }

            if (payload.type === 'camera') {
                const controls = this.controlsByTarget.get(payload.viewportKey || payload.targetId)
                    || this.controlsByTarget.get(payload.targetId)
                    || (this.controlsByTarget.size === 1
                        ? Array.from(this.controlsByTarget.values())[0]
                        : null);

                if (!controls?.object || !controls?.target) {
                    return false;
                }

                const position = Array.isArray(payload.position) ? payload.position : null;
                const cameraTarget = Array.isArray(payload.target) ? payload.target : null;
                if (!position || !cameraTarget) return false;

                this.remoteCameraApplying = true;
                controls.object.position.fromArray(position);
                controls.target.fromArray(cameraTarget);
                if (Number.isFinite(Number(payload.zoom))) controls.object.zoom = Number(payload.zoom);
                if (Number.isFinite(Number(payload.fov)) && 'fov' in controls.object) controls.object.fov = Number(payload.fov);
                controls.object.updateProjectionMatrix?.();
                originalOrbitUpdate?.call(controls);
                this.remoteCameraApplying = false;
                return true;
            }

            return false;
        } finally {
            this.applyingRemote = false;
            this.remoteCameraApplying = false;
        }
    }

    onRemoteUiState(payload = {}) {
        if (!payload || payload.pageKey !== this.pageKey) return;

        const isOwnEvent = Boolean(
            payload.socketId
            && payload.socketId === this.socket?.id
        );
        const isAuthoritativeEcho = Number(payload.stateRevision || 0) > 0;

        // cursor/hover/click/focus/press от самого себя не применяем. Но
        // authoritative echo ОБЯЗАТЕЛЬНО принимаем: он закрывает race между
        // двумя устройствами и подтверждает серверный порядок revision.
        if (isOwnEvent && !isAuthoritativeEcho) return;

        const authoritativeKey = this.getAuthoritativeEventKey(payload);
        if (authoritativeKey && !this.rememberAuthoritativeEvent(payload)) return;

        const startedAt = typeof performance !== 'undefined'
            ? performance.now()
            : Date.now();

        if (authoritativeKey) {
            this.applyRememberedAuthoritative(payload);
        } else {
            this.applyRemoteEvent(payload, { allowQueue: false });
        }

        const finishedAt = typeof performance !== 'undefined'
            ? performance.now()
            : Date.now();
        this.recordRemoteApplyDuration(finishedAt - startedAt);
    }

    clearRemoteVisuals(socketId = '') {
        const selector = [
            `[${TARGET_ATTR}][data-admin-remote-hover="true"]`,
            `[${TARGET_ATTR}][data-admin-remote-pressed="true"]`,
            `[${TARGET_ATTR}][data-admin-remote-focus="true"]`,
            `[${TARGET_ATTR}][data-admin-remote-loading="true"]`,
        ].join(',');

        document.querySelectorAll(selector).forEach((node) => {
            node.setAttribute('data-admin-remote-hover', 'false');
            node.setAttribute('data-admin-remote-pressed', 'false');
            node.setAttribute('data-admin-remote-focus', 'false');
            node.setAttribute('data-admin-remote-loading', 'false');
        });

        if (socketId) {
            let locksChanged = false;
            for (const [blockId, lock] of this.blockLocks.entries()) {
                if (String(lock?.socketId || '') === String(socketId)) {
                    this.blockLocks.delete(blockId);
                    this.ownedBlockIds.delete(blockId);
                    locksChanged = true;
                }
            }
            if (locksChanged) this.renderBlockLocks();

            this.clearRemoteCursorForSocket(socketId);
            Array.from(this.remoteSelectMirrors.keys())
                .filter((key) => key.startsWith(`${String(socketId)}:`))
                .forEach((key) => this.removeRemoteSelectMirror(key));
            return;
        }

        this.remoteSelectTimers.forEach((timer) => window.clearTimeout(timer));
        this.remoteSelectTimers.clear();
        this.remoteSelectPayloads.clear();
        this.remoteSelectMirrors.forEach((node) => node.remove());
        this.remoteSelectMirrors.clear();
        this.selectMirrorLayer?.remove();
        this.selectMirrorLayer = null;
        this.localOpenSelect = null;
        this.localSelectCursorAnchor = null;
        this.stopLocalSelectCursorHeartbeat();

        this.cursorTimers.forEach((timer) => window.clearTimeout(timer));
        this.cursorTimers.clear();
        this.remoteCursorPayloads.clear();
        this.remoteCursorSequences.clear();
        this.cursorAnchorCache.clear();
        this.cursorMotion.clear();
        this.cursorOwnerSocketByIdentity.clear();
        this.cursorIdentityBySocket.clear();
        if (this.cursorAnimationFrame) {
            window.cancelAnimationFrame(this.cursorAnimationFrame);
            this.cursorAnimationFrame = null;
        }
        if (this.cursorReflowFrame) {
            window.cancelAnimationFrame(this.cursorReflowFrame);
            this.cursorReflowFrame = null;
        }
        this.cursorNodes.clear();
        this.cursorLayer?.remove();
        this.cursorLayer = null;
        this.lockElements.clear();
        this.lockFrames.forEach((frame) => frame.remove());
        this.lockFrames.clear();
        this.lockBadges.forEach((badge) => badge.remove());
        this.lockBadges.clear();
        this.lockBadgeLayer?.remove();
        this.lockBadgeLayer = null;
        this.activityLayer?.remove();
        this.activityLayer = null;
    }

    destroy() {
        if (this.destroyed) return;
        this.destroyed = true;

        this.leaveCurrentPage();
        this.socket?.off('connect', this.onConnect);
        this.socket?.off('disconnect', this.onDisconnect);
        this.socket?.off('adminRealtime:users', this.onUiUsers);
        this.socket?.off('adminRealtime:locks', this.onUiLocks);
        this.socket?.off('adminRealtime:state', this.onRemoteUiState);
        this.socket?.off('adminRealtime:snapshot', this.onUiSnapshot);

        document.removeEventListener('pointermove', this.onPointerMove, true);
        document.removeEventListener('pointerover', this.onPointerOver, true);
        document.removeEventListener('pointerout', this.onPointerOut, true);
        document.removeEventListener('pointerdown', this.onPointerDown, true);
        document.removeEventListener('pointerup', this.onPointerUp, true);
        document.removeEventListener('pointercancel', this.onPointerCancel, true);
        document.removeEventListener('mouseleave', this.onPointerLeave, true);
        document.removeEventListener('click', this.onClick, true);
        document.removeEventListener('beforeinput', this.onBeforeInput, true);
        document.removeEventListener('input', this.onInput, true);
        document.removeEventListener('change', this.onChange, true);
        document.removeEventListener('keydown', this.onKeyDown, true);
        document.removeEventListener('focusin', this.onFocusIn, true);
        document.removeEventListener('focusout', this.onFocusOut, true);
        document.removeEventListener('toggle', this.onToggle, true);
        document.removeEventListener('scroll', this.onDocumentScroll, true);
        window.removeEventListener('scroll', this.onWindowScroll);
        window.removeEventListener('resize', this.onCursorViewportChange);
        window.removeEventListener('orientationchange', this.onCursorViewportChange);
        window.visualViewport?.removeEventListener('resize', this.onCursorViewportChange);
        window.visualViewport?.removeEventListener('scroll', this.onCursorViewportChange);
        window.removeEventListener('blur', this.onPointerLeave);

        this.observer?.disconnect();
        if (this.routeTimer) window.clearInterval(this.routeTimer);
        if (this.blockHeartbeatTimer) window.clearInterval(this.blockHeartbeatTimer);
        if (this.admin3DApplyTimer) window.clearTimeout(this.admin3DApplyTimer);
        if (this.admin3DSyncTimer) window.clearTimeout(this.admin3DSyncTimer);
        if (this.authoritativeReplayFrame) window.cancelAnimationFrame(this.authoritativeReplayFrame);
        if (this.lockReflowFrame) window.cancelAnimationFrame(this.lockReflowFrame);
        this.lockReleaseTimers.forEach((timer) => window.clearTimeout(timer));
        this.lockReleaseTimers.clear();
        this.lockFrames.forEach((frame) => frame.remove());
        this.lockFrames.clear();
        this.authoritativePendingKeys.clear();
        this.valueTimers.forEach((timer) => window.clearTimeout(timer));
        this.valueTimers.clear();
        this.authoritativeEvents.clear();
        this.authoritativeRevisions.clear();
        this.clearRemoteVisuals();
        this.reportStatus({ connected: false, users: [] });
    }
}

export const startAdminRealtimeUiBridge = ({
    getEnabled,
    getPresenceMeta,
    onStatusChange,
} = {}) => {
    activeRuntime?.destroy();
    activeRuntime = new AdminRealtimeUiRuntime({
        getEnabled,
        getPresenceMeta,
        onStatusChange,
    });

    return () => {
        if (activeRuntime) activeRuntime.destroy();
        activeRuntime = null;
    };
};

export const isApplyingRemoteAdminUi = () => (
    activeRuntime?.applyingRemote === true
);


export const getAdminRealtimeRuntimeDiagnostics = () => (
    activeRuntime?.getDiagnosticsSnapshot?.() || {
        pageKey: '',
        joinedPageKey: '',
        localRevision: 0,
        pendingQueue: 0,
        applyMs: 0,
        lastApplyMs: 0,
        authoritativeStates: 0,
        connected: false,
    }
);
